import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Helper function to get appropriate error message based on error code
const getErrorMessage = (errorCode?: string) => {
  if (!errorCode) {
    return "Image generation failed due to parameter restrictions. No credits were deducted. Please adjust your settings and try again.";
  }

  // Special case: Pro model unavailable, fallback available
  if (errorCode === 'PRO_UNAVAILABLE_FALLBACK_AVAILABLE') {
    return "FALLBACK_PROMPT"; // Special marker for showing fallback dialog
  }

  // 5xx errors - Server-side issues (Gemini's fault)
  if (errorCode === 'SERVICE_UNAVAILABLE' || errorCode === 'BAD_GATEWAY' || errorCode === 'INTERNAL_SERVER_ERROR') {
    return "Our Third-party AI service is temporarily unavailable. No credits were deducted. Please try again later.";
  }

  // 4xx errors - Client-side issues (user's fault)
  if (errorCode === 'INVALID_PARAMETERS' || errorCode === 'FORBIDDEN') {
    return "Image generation failed due to parameter restrictions. No credits were deducted. Please adjust your settings or prompt if applicable and try again.";
  }

  // Rate limiting
  if (errorCode === 'QUOTA_EXCEEDED') {
    return "API rate limit exceeded. No credits were deducted. Please wait a moment and try again.";
  }

  // Timeout
  if (errorCode === 'TIMEOUT_ERROR') {
    return "Request timed out due to high server load. No credits were deducted. Please try again.";
  }

  // Authentication issues
  if (errorCode === 'UNAUTHORIZED') {
    return "Authentication error occurred. Please refresh the page and try again.";
  }

  // Default fallback
  return "Image generation failed due to parameter restrictions. No credits were deducted. Please adjust your settings and try again.";
};
import {
  Wand2,
  ImageIcon,
  Download,
  Share2,
  Settings,
  Palette,
  Zap,
  ArrowRight,
  Play,
  Loader2,
  HelpCircle,
  Magic,
  Layers,
  Eye,
  Heart,
  Info,
  ChevronDown,
  ChevronRight,
  Type,
  Target,
  BarChart3,
  Clock,
  RotateCcw,
  Save,
  Sparkles,
  Calendar,
  Crown
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/RadixSlider';
import { MultiMediaFileSelector } from '../components/media/MultiMediaFileSelector';
import { MediaFile } from '../services/mediaService';
import { ThumbnailSelect } from '../components/ai/ThumbnailSelect';
import { ModelSelector } from '../components/ai/ModelSelector';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
// useFeatureLimit no longer needed - AI tasks are credit-based only
import { useDynamicTaskCost } from '../hooks/useTaskCost';
// TierUpgradePrompt no longer needed - AI tasks are credit-based only
import { getApiUrl } from '../utils/config';
import mediaService from '../services/mediaService';
import AuthenticatedImage from '../components/media/AuthenticatedImage';
import { useTour } from '../contexts/TourContext';
import { adStudioSteps } from '../components/onboarding/tour-steps';
import { useMobile } from '../hooks/useMobile';
import adPsychology from '../assets/ad_psychology.json';
import { useAITask } from '../contexts/AITaskContext';
import { useAdStudioPersistence } from '../hooks/useAdStudioPersistence';
import { useModels } from '../hooks/useModels';
import { AdStudioPersistence } from '../utils/adStudioPersistence';
import { style } from 'framer-motion/client';
import { jsx } from 'react/jsx-runtime';
import { div } from 'framer-motion/client';

// Creative Purpose - base options
// Creative Purpose - product-based options
const creativePurposeOptionsBase = [
  { value: 'product-showcase', label: 'Showcase', thumbnail: '/images/thumbnails/purpose-1.png' },
  { value: 'product-features', label: 'Product Features', thumbnail: '/images/thumbnails/purpose-7.png' },
  { value: 'lifestyle', label: 'Lifestyle', thumbnail: '/images/thumbnails/purpose-9.png' },
  { value: 'social-proof', label: 'Social Proof', thumbnail: '/images/thumbnails/purpose-3.png' },
  { value: 'promotional', label: 'Promotional', thumbnail: '/images/thumbnails/purpose-5.png' },
  { value: 'before-after', label: 'Before/After', thumbnail: '/images/thumbnails/purpose-6.png' },
  { value: 'seasonal-occasion', label: 'Occasion', thumbnail: '/images/thumbnails/purpose-2.png' },
  { value: 'unboxing-experience', label: 'Unboxing', thumbnail: '/images/thumbnails/purpose-8.png' },
];

// Creative Purpose - service-based options
const creativePurposeOptionsService = [
  { value: 'service-showcase', label: 'Showcase', thumbnail: '/images/thumbnails/service_purpose_1.png' },
  { value: 'problem-solution', label: 'Problem/Solution', thumbnail: '/images/thumbnails/service_purpose_2.png' },
  { value: 'trust-credibility', label: 'Trust/Credibility', thumbnail: '/images/thumbnails/service_purpose_7.png' },
  { value: 'process-explanation', label: 'Process Overview', thumbnail: '/images/thumbnails/service_purpose_4.png' },
  { value: 'event-organization', label: 'Event', thumbnail: '/images/thumbnails/service_purpose_8.png' },
  { value: 'self-branding', label: 'Self-Branding', thumbnail: '/images/thumbnails/service_purpose_self-branding.png' },
];

// Get filtered creative purpose options based on mode
const getCreativePurposeOptions = (isMultiProduct: boolean, isServiceMode: boolean) => {
  if (isServiceMode) {
    // Return service-specific options for text-only mode
    return creativePurposeOptionsService;
  }

  if (isMultiProduct) {
    // Exclude before/after for multi-product mode
    return creativePurposeOptionsBase.filter(option => option.value !== 'before-after');
  }
  return creativePurposeOptionsBase;
};

// Single Product Composition - only shown in single product mode
const singleProductCompositionOptions = [
  { value: 'clean-product-shot', label: 'Clean Product Shot', thumbnail: '/images/thumbnails/clean_product_shot.png' },
  { value: 'flat-lay', label: 'Flat Lay', thumbnail: '/images/thumbnails/flat_lay_top_down_dislay.png' },
  { value: 'hero-dramatic', label: 'Hero Dramatic', thumbnail: '/images/thumbnails/composition_4.png' },
  { value: 'studio-lighting', label: 'Studio Lighting', thumbnail: '/images/thumbnails/composition_12.jpg' },
  { value: 'depth-of-field', label: 'Depth of Field', thumbnail: '/images/thumbnails/composition_8.png' },
  { value: 'rule-of-thirds', label: 'Rule of Thirds', thumbnail: '/images/thumbnails/composition_3.jpg' },
  { value: 'symmetrical-balance', label: 'Symmetrical', thumbnail: '/images/thumbnails/composition_6.jpg' },
  { value: 'multiple-angles', label: 'Multi-Perspective', thumbnail: '/images/thumbnails/composition_11.jpg' },
];

// Multi-product arrangement - dynamic based on product count
const getArrangementOptions = (productCount: number) => {
  const baseOptions = [
    { value: 'cohesive-combination', label: 'Combine Cohesively', thumbnail: '/images/thumbnails/cohesive_combination.svg' },
    { value: 'hero-supporting', label: 'Hero + Supporting', thumbnail: '/images/thumbnails/hero_supporting.svg' },
    { value: 'linear-showcase', label: 'Linear Showcase', thumbnail: '/images/thumbnails/linear_showcase.svg' },
    { value: 'collage-style', label: 'Collage Style', thumbnail: '/images/thumbnails/collage_style.svg' },
  ];

  // Only show grid option for 3+ products
  if (productCount >= 3) {
    baseOptions.unshift({ value: 'adaptive-grid', label: 'Grid Layout', thumbnail: '/images/thumbnails/grid_auto.svg' });
  }

  return baseOptions;
};

// Visual Style - works for both single and multi-product (comprehensive 12 core styles)
const visualStyleOptions = [
  { value: 'minimalist-clean', label: 'Minimalist', thumbnail: '/images/thumbnails/minimalist_clean.jpeg' },
  { value: 'bold-vibrant', label: 'Bold & Vibrant', thumbnail: '/images/thumbnails/bold_and_vibrant.jpeg' },
  { value: 'playful-colorful', label: 'Playful', thumbnail: '/images/thumbnails/playful_colorful.jpeg' },
  { value: 'modern-corporate', label: 'Modern', thumbnail: '/images/thumbnails/modern_corporate.jpeg' },
  { value: 'organic-natural', label: 'Natural', thumbnail: '/images/thumbnails/organic_and_natural.jpeg' },
  { value: 'monochrome-artistic', label: 'Monochrome', thumbnail: '/images/thumbnails/monochrome.png' },
  { value: 'illustration', label: 'Illustration', thumbnail: '/images/thumbnails/illustration.png', hasSubStyles: true },
  { value: 'vintage-retro', label: 'Vintage', thumbnail: '/images/thumbnails/vintage_retro.jpeg' },
  { value: 'dark-moody', label: 'Dark & Moody', thumbnail: '/images/thumbnails/dark_moody.png' },
  { value: 'tech-futuristic', label: 'Tech & Futuristic', thumbnail: '/images/thumbnails/tech_futuristic.jpeg' },
  { value: 'pastel-soft', label: 'Pastel & Soft', thumbnail: '/images/thumbnails/pastel_soft.png' },
  { value: 'luxury-elegant', label: 'Luxury', thumbnail: '/images/thumbnails/luxury_and_premium.jpeg' },
];

// Illustration sub-styles - 8 different illustration types based on Domestika reference
const illustrationStyleOptions = [
  { value: 'minimalist-illustration', label: 'Minimalist', thumbnail: '/images/thumbnails/illustration/illustration-minimalist.png', description: 'Simple shapes, clean lines, and limited color palettes to convey ideas in a straightforward and elegant way' },
  { value: 'big-bold-illustration', label: 'Big & Bold', thumbnail: '/images/thumbnails/illustration/illustration-big-and-bold.png', description: 'Large, striking imagery with bright colors, thick lines, and dramatic compositions that command attention' },
  { value: '3d-illustration', label: '3D Illustration', thumbnail: '/images/thumbnails/illustration/illustration-3d.png', description: 'Computer graphics creating three-dimensional look and feel' },
  { value: 'typography-illustration', label: 'Typography', thumbnail: '/images/thumbnails/illustration/illustration-typography.png', description: 'Combines typography with illustrations to create cohesive designs that convey information effectively' },
  { value: 'retro-illustration', label: 'Retro', thumbnail: '/images/thumbnails/illustration/illustration-retro.png', description: 'Inspired by past decades with bright colors, bold graphics, and playful illustrations that evoke nostalgia' },
  { value: 'geometric-illustration', label: 'Geometric', thumbnail: '/images/thumbnails/illustration/illustration-geometric.png', description: 'Uses geometric shapes and clean lines with focus on form and structure, minimal use of color' },
  { value: 'caricature-illustration', label: 'Caricatures', thumbnail: '/images/thumbnails/illustration/illustration-caricatures.png', description: 'Exaggerated or distorted images for humor or satire, often used in editorial illustrations' },
  { value: 'cartoon-animated', label: 'Comics', thumbnail: '/images/thumbnails/illustration/illustration-comics.png', description: 'Sequential format using illustrations and text to tell stories, from simple cartoons to complex graphic novels' },
];

const platformPlacementOptions = [
  { value: 'instagram-story', label: 'Instagram Story' },
  { value: 'instagram-feed-reel', label: 'Instagram Feed / Reel' },
  { value: 'facebook-feed', label: 'Facebook Feed' },
  { value: 'linkedin-sponsored-post', label: 'LinkedIn Sponsored Post' },
  { value: 'tiktok-video-poster-frame', label: 'TikTok Video Poster Frame' },
  { value: 'youtube-thumbnail-overlay', label: 'YouTube Thumbnail / Overlay' },
  { value: 'google-display-banner', label: 'Google Display Banner' },
  { value: 'pinterest-pin', label: 'Pinterest Pin' },
];

const fontFamilyOptions = [
  { value: 'auto', label: 'Auto' },
  { value: 'sans', label: 'Sans-serif' },
  { value: 'serif', label: 'Serif' },
  { value: 'mono', label: 'Monospace' },
];

const fontSizeOptions = [
  { value: 'auto', label: 'Auto' },
  { value: '16', label: 'Small' },
  { value: '24', label: 'Medium' },
  { value: '36', label: 'Large' },
  { value: '48', label: 'Extra Large' },
];

const fontColorOptions = [
  { value: 'auto', label: 'Auto' },
  { value: 'white', label: 'White' },
  { value: 'black', label: 'Black' },
  { value: 'red', label: 'Red' },
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
];

const textPositionOptions = [
  { value: 'auto', label: 'Auto' },
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-center', label: 'Top Center' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-center', label: 'Bottom Center' },
  { value: 'bottom-right', label: 'Bottom Right' },
];

const eventTypeOptions = [
  { value: 'workshop', label: 'Workshop' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'conference', label: 'Conference' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'training', label: 'Training Session' },
  { value: 'networking', label: 'Networking Event' },
  { value: 'product-launch', label: 'Product Launch' },
  { value: 'consultation', label: 'Consultation Session' },
  { value: 'masterclass', label: 'Masterclass' },
  { value: 'demo', label: 'Demo/Presentation' },
];

const enhancementPrompts = [
  "Create a stunning product showcase with cinematic lighting",
  "Transform this into a lifestyle scene with modern aesthetics",
  "Generate a premium, luxury advertisement",
  "Create an energetic, vibrant marketing visual",
];

interface ProductEnhancementFormData {
  image_file_id?: string;
  image_file_ids?: string[];
  prompt: string;
  adIntent: string;
  product_features?: string;
  product_features_title?: string;
  formatComposition: string;
  styleArtDirection: string;
  platformPlacement: string;
  influenceTuning: { [key: string]: number };
  enable_text_overlay: boolean;
  text_overlay_content: string;
  font_family: string;
  font_size: string;
  font_color: string;
  text_position: string;
  num_images: number;
  preserve_subject: boolean;
  // Multi-product fields
  product_arrangement?: string;
  product_relationship?: string;
  product_priorities?: number[];
  product_labels?: string[];
  background_complexity?: string;
  color_harmony?: string;
  // Reference image fields
  reference_image_ids?: string[];
  reference_style_weight?: number;
  reference_composition_weight?: number;
  // Credibility image fields
  credibility_image_ids?: string[];
  // Event organization fields
  event_name?: string;
  event_date?: string;
  event_type?: string;
  event_location?: string;
  // Self-branding fields
  self_image_id?: string;
  // Image quality fields
  aspect_ratio?: string;
  resolution?: string;
  omit_text_overlay?: boolean;
}

const AdStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addTask, getTasksByPage, isTaskRunning, getRecentCompletedTask, updateTask, removeTask } = useAITask();
  const { isARMode, arViewerUrl, inputImageUrl, sourceMediaId } = location.state || {};
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  const [selectedImage, setSelectedImage] = useState<MediaFile[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<MediaFile[]>([]);
  const [selectedReferenceImages, setSelectedReferenceImages] = useState<MediaFile[]>([]);
  const [selectedCredibilityImages, setSelectedCredibilityImages] = useState<MediaFile[]>([]);
  const [selectedSelfImage, setSelectedSelfImage] = useState<MediaFile[]>([]);
  const [showFallbackDialog, setShowFallbackDialog] = useState(false);
  const [fallbackTaskId, setFallbackTaskId] = useState<string | null>(null);
  const [multiProductMode, setMultiProductMode] = useState(false);
  const [textOnlyMode, setTextOnlyMode] = useState(false);
  const [textPrompt, setTextPrompt] = useState('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedEnhancedImages, setGeneratedEnhancedImages] = useState<string[]>([]);

  const [showOutputSettingsModal, setShowOutputSettingsModal] = useState(false);
  const [tempNumImages, setTempNumImages] = useState<number>(1);
  const [tempAspectRatio, setTempAspectRatio] = useState<string>('auto');
  const [tempResolution, setTempResolution] = useState<string>('1k');
  const [tempOmitTextOverlay, setTempOmitTextOverlay] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [generationMessage, setGenerationMessage] = useState<string>('Understanding Creative Vision');
  const [showInfluenceTuning, setShowInfluenceTuning] = useState(false);
  const [showInfluenceTuningModal, setShowInfluenceTuningModal] = useState(false);
  const [showIllustrationModal, setShowIllustrationModal] = useState(false);
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [showReferenceImages, setShowReferenceImages] = useState(false);
  const [useBrandify, setUseBrandify] = useState(false);
  const [brandKit, setBrandKit] = useState<any>(null);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const hasShownResumeToast = useRef(false);
  const [completedTaskIds, setCompletedTaskIds] = useState<Set<string>>(new Set());
  const [pollingTaskIds, setPollingTaskIds] = useState<Set<string>>(new Set());
  const [highlightMissingFields, setHighlightMissingFields] = useState<{
    creativePurpose: boolean;
  }>({ creativePurpose: false });

  // Refs for auto-resizing textareas
  const textPromptRef = useRef<HTMLTextAreaElement>(null);
  const customDescriptionRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = '60px'; // Reset to min height
      textarea.style.height = `${Math.max(60, textarea.scrollHeight)}px`;
    }
  };

  // Function to clear all field highlights
  const clearFieldHighlights = () => {
    setHighlightMissingFields({ creativePurpose: false });
  };

  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null);
  const initialProgress = useRef(0); // Store initial progress for restored tasks

  const { currentClient } = useClient();
  const { refreshUser, user: currentUser } = useAuth();

  // Get available models
  const { availableModels } = useModels();

  // Auto-select default model when models load
  useEffect(() => {
    if (availableModels.length > 0 && !selectedModelId) {
      // Find the default model (is_default = true)
      let modelToSelect = availableModels.find(m => m.is_default);
      
      if (!modelToSelect) {
        console.log('⚠️ AdStudioPage: No default model found, selecting first available model');
        // Fallback: select the first available model (they're already sorted by sort_order from backend)
        modelToSelect = availableModels[0];
      }
      
      if (modelToSelect) {
        console.log('🎯 AdStudioPage: Auto-selecting model:', modelToSelect.display_name, modelToSelect.model_id);
        setSelectedModelId(modelToSelect.model_id);
      }
    }
  }, [availableModels, selectedModelId]);

  // Dynamic generation messages based on progress
  useEffect(() => {
    if (isEnhancing) {
      if (generationProgress < 20) {
        setGenerationMessage('Understanding Creative Vision...');
      } else if (generationProgress < 50) {
        setGenerationMessage('Gathering Components...');
      } else if (generationProgress < 75) {
        setGenerationMessage('Crafting Masterpiece...');
      } else if (generationProgress < 88) {
        setGenerationMessage('Adding The Secret Sauce...');
      } else if (generationProgress < 99) {
        setGenerationMessage('Finalizing Details...');
      } else {
        setGenerationMessage('Done!');
      }
    }
  }, [generationProgress, isEnhancing]);



  // Load completed task IDs from localStorage on mount to prevent duplicate toasts across page navigation
  // SECURITY FIX: Make this user-specific to prevent cross-user contamination
  useEffect(() => {
    if (!currentUser?.id || !currentClient?.id) return;

    const userSpecificKey = `adStudio_completedTaskIds_${currentUser.id}_${currentClient.id}`;
    const savedCompletedIds = localStorage.getItem(userSpecificKey);
    if (savedCompletedIds) {
      try {
        const parsedIds = JSON.parse(savedCompletedIds);
        setCompletedTaskIds(new Set(parsedIds));
        console.log('📋 Loaded user-specific completed task IDs:', parsedIds.length);
      } catch (error) {
        console.warn('Failed to parse completed task IDs from localStorage');
      }
    }

    // Clear any old non-user-specific completed task IDs
    const oldKey = 'adStudio_completedTaskIds';
    if (localStorage.getItem(oldKey)) {
      console.log('🗑️ Clearing old non-user-specific completed task IDs');
      localStorage.removeItem(oldKey);
    }
  }, [currentUser?.id, currentClient?.id]);

  // Clear any foreign state immediately when component mounts with user context
  useEffect(() => {
    if (currentUser?.id && currentClient?.id) {
      // Check if there's any stored state that belongs to a different user
      const storedState = localStorage.getItem(`adStudio_state_${currentUser.id}_${currentClient.id}`);
      if (!storedState) {
        // No state for current user, but check if there are any other Ad Studio states
        const allKeys = Object.keys(localStorage);
        const otherAdStudioKeys = allKeys.filter(key =>
          key.startsWith('adStudio_state_') &&
          !key.includes(`${currentUser.id}_${currentClient.id}`)
        );

        if (otherAdStudioKeys.length > 0) {
          console.log('🗑️ Found foreign Ad Studio states, clearing them:', otherAdStudioKeys);
          otherAdStudioKeys.forEach(key => localStorage.removeItem(key));

          // Also clear completed task IDs that might be from other users
          const completedTaskKeys = allKeys.filter(key => key.includes('adStudio_completedTaskIds'));
          completedTaskKeys.forEach(key => localStorage.removeItem(key));
        }
      }
    }
  }, [currentUser?.id, currentClient?.id]);
  const { toast } = useToast();
  const { startTour, restartTour } = useTour();
  const isMobile = useMobile();

  // Dynamic task cost based on Brandify usage (credit-based only)
  const {
    cost: baseCost,
    canUseFeature,
    usage,
    limit,
    remaining,
    isUnlimited,
    isLimitReached,
    taskName
  } = useDynamicTaskCost(useBrandify);

  // Fetch brand kit when Brandify is enabled
  useEffect(() => {
    const fetchBrandKit = async () => {
      if (useBrandify && currentClient) {
        try {
          // Fetch actual brand kit data from the backend
          const response = await fetch(`${getApiUrl()}/api/v1/clients/${currentClient.id}/brand-kit`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'X-Client-ID': currentClient.id,
            },
          });

          if (response.ok) {
            const brandKitData = await response.json();
            setBrandKit({
              brand_name: brandKitData.brand_name || currentClient.name || 'Default Brand Name',
              brand_voice: brandKitData.brand_voice || currentClient.bio || 'Premium, modern, professional brand focused on quality and innovation',
              website_url: brandKitData.website || currentClient.website || null,
              brand_colors: brandKitData.brand_colors ? Object.values(brandKitData.brand_colors) : ['#6366F1', '#8B5CF6', '#EC4899'],
              logo_media_id: brandKitData.logo_media_id || null,
              // Enhanced brand kit fields
              target_audience: brandKitData.target_audience || null,
              industry: brandKitData.industry || null,
              brand_personality: brandKitData.brand_personality || null,
              primary_goal: brandKitData.primary_goal || null
            });
          } else {
            // Fallback to client data if brand kit fetch fails
            setBrandKit({
              brand_name: currentClient.name || 'Default Brand Name',
              brand_voice: currentClient.bio || 'Premium, modern, professional brand focused on quality and innovation',
              website_url: currentClient.website || null,
              brand_colors: ['#6366F1', '#8B5CF6', '#EC4899'],
              logo_media_id: null,
              // Enhanced brand kit fields (null for fallback)
              target_audience: null,
              industry: null,
              brand_personality: null,
              primary_goal: null
            });
          }
        } catch (error) {
          console.error('Error fetching brand kit:', error);
          // Fallback to client data
          setBrandKit({
            brand_name: currentClient.name || 'Default Brand Name',
            brand_voice: currentClient.bio || 'Premium, modern, professional brand focused on quality and innovation',
            website_url: currentClient.website || null,
            brand_colors: ['#6366F1', '#8B5CF6', '#EC4899'],
            logo_media_id: null,
            // Enhanced brand kit fields (null for error fallback)
            target_audience: null,
            industry: null,
            brand_personality: null,
            primary_goal: null
          });
        }
      } else {
        setBrandKit(null);
      }
    };

    fetchBrandKit();
  }, [useBrandify, currentClient]);

  const { register, handleSubmit, setValue, watch, control, getValues, reset, formState: { errors } } = useForm<ProductEnhancementFormData>({
    mode: 'onChange',
    defaultValues: {
      prompt: '',
      adIntent: '',
      formatComposition: '',
      styleArtDirection: '',
      platformPlacement: '',
      num_images: 1,
      preserve_subject: true,
      product_features: '',
      product_features_title: '',
      influenceTuning: Object.keys(adPsychology.psychological_levers).reduce((acc, key) => ({ ...acc, [key]: 0 }), {}),
      enable_text_overlay: false,
      text_overlay_content: '',
      font_family: 'auto',
      font_size: 'auto',
      font_color: 'auto',
      text_position: 'auto',
      // Multi-product defaults
      background_complexity: 'minimal',
      color_harmony: 'auto',
      // Reference image defaults
      reference_style_weight: 0.7,
      reference_composition_weight: 0.5,
      // Event organization defaults
      event_name: '',
      event_date: '',
      event_type: '',
      event_location: '',
      // Image quality defaults
      aspect_ratio: 'auto',
      resolution: '1k',
      omit_text_overlay: false,
    }
  });

  // Save state whenever generated images change (moved after form declaration)
  useEffect(() => {
    if (generatedEnhancedImages.length > 0 && currentUser?.id && currentClient?.id) {
      console.log('💾 Generated images changed, saving state:', generatedEnhancedImages.length, 'images');
      console.log('💾 Generated images URLs:', generatedEnhancedImages);

      // Save the current state including the new generated images
      const currentState = {
        formData: getValues(),
        multiProductMode,
        textOnlyMode,
        useBrandify,
        showAdvanced,
        showInfluenceTuning,
        selectedImage,
        selectedProducts,
        selectedReferenceImages,
        textPrompt,
        generatedImages: generatedEnhancedImages, // This should contain the actual URLs
        generationProgress,
        generationStartTime,
        userId: currentUser.id,
        clientId: currentClient.id,
        lastUpdated: new Date().toISOString()
      };

      console.log('💾 Full state being saved:', {
        ...currentState,
        generatedImages: currentState.generatedImages
      });

      const userSpecificKey = `adStudio_state_${currentUser.id}_${currentClient.id}`;
      localStorage.setItem(userSpecificKey, JSON.stringify(currentState));
      console.log('💾 State saved with generated images to:', userSpecificKey);

      // Verify what was actually saved
      const savedState = JSON.parse(localStorage.getItem(userSpecificKey) || '{}');
      console.log('💾 Verification - saved generatedImages:', savedState.generatedImages);
    }
  }, [generatedEnhancedImages, currentUser?.id, currentClient?.id, multiProductMode, textOnlyMode, useBrandify, showAdvanced, showInfluenceTuning, selectedImage, selectedProducts, selectedReferenceImages, textPrompt, generationProgress, generationStartTime, getValues]);

  // Debug localStorage and clear foreign state when user context changes
  useEffect(() => {
    if (currentUser?.id && currentClient?.id) {
      // Debug: Show what's in localStorage
      const userSpecificKey = `adStudio_state_${currentUser.id}_${currentClient.id}`;
      const savedState = localStorage.getItem(userSpecificKey);
      console.log('🔍 DEBUG: Current user state in localStorage:', userSpecificKey);
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          console.log('🔍 DEBUG: Parsed state:', {
            hasFormData: !!parsed.formData,
            selectedImageCount: parsed.selectedImage?.length || 0,
            generatedImagesCount: parsed.generatedImages?.length || 0,
            generatedImages: parsed.generatedImages
          });
        } catch (e) {
          console.log('🔍 DEBUG: Failed to parse saved state');
        }
      } else {
        console.log('🔍 DEBUG: No saved state found');
      }

      // Get all localStorage keys
      const allKeys = Object.keys(localStorage);
      console.log('🔍 DEBUG: All localStorage keys:', allKeys.filter(key => key.includes('adStudio')));

      // Find foreign Ad Studio state keys (not belonging to current user)
      const currentUserPrefix = `adStudio_state_${currentUser.id}_${currentClient.id}`;
      const currentUserCompletedTasksKey = `adStudio_completedTaskIds_${currentUser.id}_${currentClient.id}`;

      const foreignKeys = allKeys.filter(key =>
        (key.startsWith('adStudio_state_') && !key.startsWith(currentUserPrefix)) ||
        (key.startsWith('adStudio_completedTaskIds_') && key !== currentUserCompletedTasksKey) ||
        (key === 'adStudio_completedTaskIds') // Old non-user-specific key
      );

      if (foreignKeys.length > 0) {
        console.log('🗑️ Clearing foreign Ad Studio data:', foreignKeys);
        foreignKeys.forEach(key => localStorage.removeItem(key));
      }
    }
  }, [currentUser?.id, currentClient?.id]);

  // Initialize persistence system
  const { clearPersistedState, getStateSummary, hasPersistedState, saveStateForNavigation } = useAdStudioPersistence({
    form: { register, handleSubmit, setValue, watch, control, getValues, reset, formState: { errors } },
    selectedImage,
    selectedProducts,
    selectedReferenceImages,
    selectedCredibilityImages,
    multiProductMode,
    textOnlyMode,
    useBrandify,
    showAdvanced,
    showInfluenceTuning,
    textPrompt,
    generatedImages: generatedEnhancedImages,
    setSelectedImage,
    setSelectedProducts,
    setSelectedReferenceImages,
    setSelectedCredibilityImages,
    setMultiProductMode,
    setTextOnlyMode,
    setUseBrandify,
    setShowAdvanced,
    setShowInfluenceTuning,
    setTextPrompt,
    setGeneratedImages: setGeneratedEnhancedImages,
    isEnhancing,
    hasActiveSession,
    setHasActiveSession,
    generationProgress,
    generationStartTime,
    setGenerationProgress,
    setGenerationStartTime,
    // Pass user context for security
    currentUserId: currentUser?.id,
    currentClientId: currentClient?.id,
  });

  // Nuclear reset function to clear everything and fix stuck states
  const performNuclearReset = useCallback(() => {
    console.log('💥 NUCLEAR RESET: Clearing all states and stuck tasks');

    try {
      // 1. Clear all persisted state
      clearPersistedState();

      // 2. Clear ALL AdStudio states from localStorage (nuclear cleanup)
      AdStudioPersistence.clearAllUserStates();

      // 3. Remove all active tasks for this page to clear stuck processing states
      const allTasks = getTasksByPage('ad-studio');
      allTasks.forEach(task => {
        console.log('🗑️ Removing stuck task:', task.id, task.status);
        removeTask(task.id);
      });

      // 4. Clear any additional localStorage keys that might cause issues
      const keysToRemove = [
        'adStudio_completedTaskIds',
        'adStudio_generationProgress',
        'adStudio_generationStartTime',
        'adStudio_lastGeneratedImages',
        'adStudio_sessionState'
      ];

      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
          // Also try with user/client prefixes
          if (currentUser?.id && currentClient?.id) {
            localStorage.removeItem(`${key}_${currentUser.id}_${currentClient.id}`);
          }
        } catch (error) {
          console.warn(`Failed to remove ${key}:`, error);
        }
      });

      // 5. Reset all form and component state
      reset();
      setSelectedImage([]);
      setSelectedProducts([]);
      setSelectedReferenceImages([]);
      setSelectedCredibilityImages([]);
      setSelectedSelfImage([]);
      setGeneratedEnhancedImages([]);
      setTextPrompt('');
      setMultiProductMode(false);
      setTextOnlyMode(false);
      setUseBrandify(false);
      setShowAdvanced(false);
      setShowInfluenceTuning(false);
      setHasActiveSession(true);

      // 6. Clear any generation state
      setIsSubmitting(false);
      setIsEnhancing(false);
      setGenerationProgress(0);
      setGenerationStartTime(null);

      console.log('✅ Nuclear reset completed successfully');

      toast({
        title: "🚀 Complete Reset Successful",
        description: "All options cleared. You can now start from a clean slate.",
      });

    } catch (error) {
      console.error('❌ Nuclear reset failed:', error);
      toast({
        title: "Reset Error",
        description: "Some states may not have been cleared. Try refreshing the page.",
        variant: "destructive",
      });
    }
  }, [clearPersistedState, getTasksByPage, removeTask, reset, currentUser?.id, currentClient?.id]);

  const watchedInfluenceTuning = watch('influenceTuning');
  const watchedEnableTextOverlay = watch('enable_text_overlay');
  const watchedAdIntent = watch('adIntent');

  // Debug form values to track unwanted resets
  const allFormValues = watch();

  // Track form changes for debugging
  React.useEffect(() => {
    if (watchedAdIntent || allFormValues.product_features || allFormValues.product_features_title) {
      console.log('🔍 Form values changed:', {
        adIntent: watchedAdIntent,
        product_features: allFormValues.product_features,
        product_features_title: allFormValues.product_features_title
      });
    }
  }, [watchedAdIntent, allFormValues.product_features, allFormValues.product_features_title]);

  // Watch num_images and resolution to calculate total cost and time
  const numImages = watch('num_images');
  const selectedResolution = watch('resolution');

  // Get selected model's cost
  const selectedModel = availableModels.find(m => m.model_id === selectedModelId);
  const modelCost = selectedModel?.credit_cost || baseCost;

  // Calculate total cost based on user tier, model, and resolution
  const isPaidUser = currentUser?.tier?.name?.toLowerCase() !== 'free' || currentUser?.is_beta_tester;

  // Apply resolution multiplier to the model's base cost first
  const resolutionMultipliers: { [key: string]: number } = {
    '1k': 1.0,  // 1x base cost
    '2k': 1.5,  // 1.5x base cost
    '4k': 2.0   // 2x base cost
  };
  
  const multiplier = selectedResolution ? (resolutionMultipliers[selectedResolution] || 1.0) : 1.0;
  // Round to nearest multiple of 5 for clean numbers
  let costPerImage = Math.round((modelCost * multiplier) / 5) * 5;
  
  // Add Brandify cost AFTER resolution calculation for free tier users
  if (!isPaidUser && useBrandify) {
    // For free tier, Brandify adds flat 5 credits per image
    const brandifyAdditionalCost = 5;
    costPerImage += brandifyAdditionalCost;
  }
  
  const totalCost = costPerImage * (numImages || 1);

  // Calculate estimated processing time
  const getEstimatedTime = (numImages: number, useBrandify: boolean) => {
    const baseTime = useBrandify ? 12 : 8; // seconds - adjusted based on actual performance
    return baseTime * numImages;
  };

  const estimatedTimeSeconds = getEstimatedTime(numImages || 1, useBrandify);
  const estimatedTimeDisplay = estimatedTimeSeconds >= 60
    ? `${Math.ceil(estimatedTimeSeconds / 60)}m`
    : `${estimatedTimeSeconds}s`;

  // Perceived progress tracking effect (snappy and responsive)
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isEnhancing && generationStartTime) {
      interval = setInterval(() => {
        const elapsed = (Date.now() - generationStartTime) / 1000;

        // Get the initial progress from ref (50% for restored tasks, 0% for new tasks)
        const startingProgress = initialProgress.current;

        // Debug logging
        if (elapsed < 2) {
          console.log('🔍 Progress debug:', { elapsed, startingProgress, currentProgress: generationProgress });
        }

        // If we have a starting progress (restored task), adjust the curve to continue from there
        let calculatedProgress;
        if (startingProgress > 0) {
          // For restored tasks: progress from startingProgress to 90%
          const remainingProgress = 90 - startingProgress;
          const progressRate = remainingProgress / 15; // Complete remaining in ~15 seconds
          calculatedProgress = startingProgress + (elapsed * progressRate);
        } else {
          // For new tasks: slower, more realistic curve from 0%
          if (elapsed < 5) {
            // 0-20% in first 5 seconds
            calculatedProgress = (elapsed / 5) * 20;
          } else if (elapsed < 12) {
            // 20-50% in next 7 seconds
            calculatedProgress = 20 + ((elapsed - 5) / 7) * 30;
          } else if (elapsed < 20) {
            // 50-75% in next 8 seconds
            calculatedProgress = 50 + ((elapsed - 12) / 8) * 25;
          } else if (elapsed < 30) {
            // 75-88% in next 10 seconds (slowing down)
            calculatedProgress = 75 + ((elapsed - 20) / 10) * 13;
          } else {
            // 88-90% after 30 seconds (very slow final stretch)
            calculatedProgress = Math.min(88 + ((elapsed - 30) / 20) * 2, 90);
          }
        }

        setGenerationProgress(Math.min(calculatedProgress, 90));
      }, 200); // Update every 200ms for smoother animation
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isEnhancing, generationStartTime]);

  // Save generation timing state periodically during generation
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isEnhancing && generationStartTime) {
      interval = setInterval(() => {
        // Save current timing state to localStorage for restoration
        const userSpecificKey = currentUser?.id && currentClient?.id
          ? `adStudio_state_${currentUser.id}_${currentClient.id}`
          : 'adStudio_state';
        const currentState = JSON.parse(localStorage.getItem(userSpecificKey) || '{}');
        const updatedState = {
          ...currentState,
          generationStartTime,
          generationProgress,
          lastUpdated: new Date().toISOString()
        };
        localStorage.setItem(userSpecificKey, JSON.stringify(updatedState));
      }, 2000); // Save every 2 seconds during generation
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isEnhancing, generationStartTime, generationProgress]);

  // Check for running tasks on component mount to restore loading state (only for cross-navigation)
  useEffect(() => {
    // Only check for running tasks if we're not in an active session and not already enhancing
    if (hasActiveSession || isEnhancing) return;

    const runningTask = getTasksByPage('ad-studio').find(task =>
      task.status === 'pending' || task.status === 'processing'
    );

    if (runningTask && !hasShownResumeToast.current) {
      console.log('🔄 Found running task from previous session, restoring loading state:', runningTask.id);

      // Delay setting enhancing state to allow persistence system to restore first
      setTimeout(() => {
        setIsEnhancing(true);
        // Only clear generated images if we're actually resuming a task
        // The persistence system should have already restored any completed results
        console.log('🔄 Starting task resumption after persistence restoration');
      }, 100);

      hasShownResumeToast.current = true; // Prevent duplicate toasts

      // Restore generation timing state from persistence
      const userSpecificKey = currentUser?.id && currentClient?.id
        ? `adStudio_state_${currentUser.id}_${currentClient.id}`
        : 'adStudio_state';
      const savedState = JSON.parse(localStorage.getItem(userSpecificKey) || '{}');
      console.log('🔍 Debug saved state:', savedState);

      if (savedState.generationStartTime && savedState.generationProgress !== undefined) {
        console.log('⏱️ Restoring generation timing for resumed task:', {
          originalStartTime: new Date(savedState.generationStartTime).toISOString(),
          savedProgress: savedState.generationProgress,
          timeAgo: (Date.now() - savedState.generationStartTime) / 1000 + ' seconds'
        });

        // Start from middle progress to show work has been happening
        setGenerationStartTime(Date.now());
        initialProgress.current = 50; // Store initial progress for restored tasks
        setGenerationProgress(50); // Always start restored tasks at 50% to show progress

        console.log('⏱️ Restored generation starting from 50% to show ongoing work');
      } else {
        // If no saved timing, start fresh
        console.log('⏱️ No saved timing, starting fresh');
        setGenerationStartTime(Date.now());
        setGenerationProgress(0);
      }

      // Start polling for this task
      pollTaskStatus(runningTask.id);

      toast({
        title: "Generation Resumed",
        description: "Your content generation from the previous session is still in progress.",
      });
    } else if (!hasActiveSession && generatedEnhancedImages.length === 0) {
      // If no running task but we have persisted state, check if we should clear old results
      const hasPersistedResults = hasPersistedState();
      if (hasPersistedResults) {
        console.log('📂 No running task, keeping persisted results');
      }
    }
  }, []); // Only run once on mount

  // Handle AR mode state persistence
  useEffect(() => {
    if (isARMode && arViewerUrl && inputImageUrl && sourceMediaId) {
      console.log('🎯 AR mode detected, persisting AR state');
      AdStudioPersistence.setARMode({
        isARMode: true,
        arViewerUrl,
        inputImageUrl,
        sourceMediaId,
      });

      // If we have a source media ID, try to load it as the selected image
      if (sourceMediaId && currentClient) {
        mediaService.getMediaFile(sourceMediaId, currentClient.id)
          .then(mediaFile => {
            if (mediaFile) {
              console.log('📷 Loading AR source image:', mediaFile);
              setSelectedImage([mediaFile]);
            }
          })
          .catch(error => {
            console.error('Failed to load AR source image:', error);
          });
      }
    }
  }, [isARMode, arViewerUrl, inputImageUrl, sourceMediaId, currentClient]);









  const fetchTaskResults = async (taskId: string) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient?.id || '',
        },
      });

      if (response.ok) {
        const taskData = await response.json();

        // Check if task has output media files
        if (taskData.output_media_files && taskData.output_media_files.length > 0) {
          const imageUrls = taskData.output_media_files.map((file: any) => file.file_url);
          console.log('🔍 Setting generated images (direct):', imageUrls);
          setGeneratedEnhancedImages(imageUrls);
          setIsEnhancing(false);



          // Store results in task context
          console.log('💾 Storing task results:', { taskId, imageUrls });
          updateTask(taskId, {
            status: 'completed',
            completed_at: new Date().toISOString(),
            results: imageUrls
          });

          // Toast is handled by pollTaskStatus to avoid duplicates
          // toast({
          //   title: "🎉 Advertisement Created!",
          //   description: `Generated ${imageUrls.length} stunning variation${imageUrls.length > 1 ? 's' : ''}!`
          // });
        }
      }
    } catch (error) {
      console.error('Error fetching task results:', error);
      setIsEnhancing(false);
    }
  };

  useEffect(() => {
    if (selectedReferenceImages.length > 0 && showReferenceImages) {
      setValue('reference_image_ids', selectedReferenceImages.map(file => file.id));
    } else {
      setValue('reference_image_ids', undefined);
    }
  }, [selectedReferenceImages, showReferenceImages, setValue]);

  // Clear reference images when toggle is turned off
  useEffect(() => {
    if (!showReferenceImages) {
      setSelectedReferenceImages([]);
      setValue('reference_image_ids', undefined);
      setValue('reference_style_weight', 0.7);
      setValue('reference_composition_weight', 0.5);
    }
  }, [showReferenceImages, setSelectedReferenceImages, setValue]);

  useEffect(() => {
    if (selectedCredibilityImages.length > 0 && watchedAdIntent === 'trust-credibility') {
      setValue('credibility_image_ids', selectedCredibilityImages.map(file => file.id));
    } else {
      setValue('credibility_image_ids', undefined);
    }
  }, [selectedCredibilityImages, watchedAdIntent, setValue]);

  // Sync selected self-image with form
  useEffect(() => {
    if (selectedSelfImage.length > 0 && watchedAdIntent === 'self-branding') {
      setValue('self_image_id', selectedSelfImage[0].id);
    } else {
      setValue('self_image_id', undefined);
    }
  }, [selectedSelfImage, watchedAdIntent, setValue]);

  // Clear credibility images when switching away from trust-credibility
  useEffect(() => {
    if (watchedAdIntent !== 'trust-credibility' && selectedCredibilityImages.length > 0) {
      setSelectedCredibilityImages([]);
      setValue('credibility_image_ids', undefined);
    }
  }, [watchedAdIntent, selectedCredibilityImages.length, setSelectedCredibilityImages, setValue]);

  // Clear self-image when switching away from self-branding
  useEffect(() => {
    if (watchedAdIntent !== 'self-branding' && selectedSelfImage.length > 0) {
      setSelectedSelfImage([]);
      setValue('self_image_id', undefined);
    }
  }, [watchedAdIntent, selectedSelfImage.length, setSelectedSelfImage, setValue]);

  // Set form values based on selected images
  useEffect(() => {
    console.log('🔄 Setting form values based on selected images:', {
      textOnlyMode,
      multiProductMode,
      selectedImageCount: selectedImage.length,
      selectedProductsCount: selectedProducts.length,

    });

    if (textOnlyMode) {
      // Clear image file IDs in text-only mode
      console.log('📝 Text-only mode: clearing image file IDs');
      setValue('image_file_id', undefined);
      setValue('image_file_ids', undefined);
    } else if (multiProductMode && selectedProducts.length > 0) {
      console.log('📦 Multi-product mode: setting image_file_ids to', selectedProducts.map(file => file.id));
      setValue('image_file_ids', selectedProducts.map(file => file.id));
      setValue('image_file_id', undefined);
    } else if (!multiProductMode && selectedImage.length > 0) {
      console.log('🖼️ Single image mode: setting image_file_id to', selectedImage[0].id);
      setValue('image_file_id', selectedImage[0].id);
      setValue('image_file_ids', undefined);
    } else {
      console.log('🚫 No images selected: clearing all image file IDs');
      setValue('image_file_id', undefined);
      setValue('image_file_ids', undefined);
    }
  }, [selectedImage, selectedProducts, multiProductMode, textOnlyMode, setValue]);

  const pollTaskStatus = (taskId: string) => {
    // Prevent multiple polling for the same task
    if (pollingTaskIds.has(taskId)) {
      console.log('🔄 Already polling task:', taskId);
      return;
    }

    console.log('🔄 Starting to poll task:', taskId);
    setPollingTaskIds(prev => new Set(prev).add(taskId));

    const startTime = Date.now();
    const maxPollingTime = 10 * 60 * 1000; // 10 minutes maximum

    const interval = setInterval(async () => {
      // Check if we've been polling for too long
      if (Date.now() - startTime > maxPollingTime) {
        clearInterval(interval);
        setPollingTaskIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(taskId);
          return newSet;
        });
        setIsEnhancing(false);
        toast({
          title: "Processing Timeout",
          description: "The generation is taking longer than expected. Please check the Tasks page for updates.",
          variant: "destructive"
        });
        return;
      }
      try {
        const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'X-Client-ID': currentClient?.id || '',
          },
        });

        if (!response.ok) throw new Error('Failed to fetch task status');
        const task = await response.json();

        if (task.status && task.status.toUpperCase() === 'COMPLETED') {
          clearInterval(interval);
          setPollingTaskIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskId);
            return newSet;
          });
          if (task.output_media_file_ids && task.output_media_file_ids.length > 0 && currentClient) {
            // Get all generated images
            const imageUrls = [];
            for (const fileId of task.output_media_file_ids) {
              try {
                const mediaFile = await mediaService.getMediaFile(fileId, currentClient.id);
                if (mediaFile) {
                  imageUrls.push(mediaFile.file_url || mediaFile.thumbnail_url);
                }
              } catch (error) {
                console.warn(`Failed to load media file ${fileId}:`, error);
              }
            }
            console.log('🔍 Setting generated images:', imageUrls);
            setGeneratedEnhancedImages(imageUrls);

            // Immediately save state after setting generated images
            console.log('💾 Triggering immediate state save after image generation');
            setTimeout(() => {
              if (currentUser?.id && currentClient?.id) {
                const immediateState = {
                  formData: getValues(),
                  multiProductMode,
                  textOnlyMode,
                  useBrandify,
                  showAdvanced,
                  showInfluenceTuning,
                  selectedImage,
                  selectedProducts,
                  selectedReferenceImages,
                  textPrompt,
                  generatedImages: imageUrls, // Use the imageUrls directly
                  generationProgress,
                  generationStartTime,
                  userId: currentUser.id,
                  clientId: currentClient.id,
                  lastUpdated: new Date().toISOString()
                };

                const userSpecificKey = `adStudio_state_${currentUser.id}_${currentClient.id}`;
                localStorage.setItem(userSpecificKey, JSON.stringify(immediateState));
                console.log('💾 IMMEDIATE save completed with generated images:', imageUrls);
              }
            }, 100);

            // Refresh user data to update credits after successful completion
            try {
              await refreshUser();
            } catch (refreshError) {
              console.warn('Failed to refresh user data after successful completion:', refreshError);
            }

            // Only show completion toast if we haven't already shown it for this task
            if (!completedTaskIds.has(taskId)) {
              console.log('🎉 Showing completion toast for task:', taskId);
              const newCompletedIds = new Set(completedTaskIds).add(taskId);
              setCompletedTaskIds(newCompletedIds);

              // Save to localStorage to persist across page navigation (user-specific)
              if (currentUser?.id && currentClient?.id) {
                const userSpecificKey = `adStudio_completedTaskIds_${currentUser.id}_${currentClient.id}`;
                localStorage.setItem(userSpecificKey, JSON.stringify([...newCompletedIds]));
              }

              toast({ title: "🎉 Ready to Shine!", description: `Your ${imageUrls.length} stunning visuals${imageUrls.length > 1 ? 's are' : ' is'} ready to captivate your audience!` });
            } else {
              console.log('🚫 Skipping duplicate completion toast for task:', taskId);
            }
          } else {
            // Task completed but no output files - this is the "no images generated" case
            setGenerationProgress(100);
            setTimeout(() => setIsEnhancing(false), 500);

            // Refresh user data (no credits should be deducted for failed generation)
            try {
              await refreshUser();
            } catch (refreshError) {
              console.warn('Failed to refresh user data after failed generation:', refreshError);
            }

            toast({
              title: "Generation Failed",
              description: getErrorMessage(task.error_code),
              variant: "destructive"
            });
          }
          setGenerationProgress(100);
          setTimeout(() => setIsEnhancing(false), 500);
        } else if (task.status && (task.status.toLowerCase() === 'failed' || task.status.toUpperCase() === 'FAILED')) {
          clearInterval(interval);
          setPollingTaskIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskId);
            return newSet;
          });
          setIsEnhancing(false);

          // Refresh user data to update credits (since no credits were deducted for failed tasks)
          try {
            await refreshUser();
          } catch (refreshError) {
            console.warn('Failed to refresh user data after task failure:', refreshError);
          }

          const errorMessage = getErrorMessage(task.error_code);
          if (errorMessage === "FALLBACK_PROMPT") {
            // Show fallback dialog instead of toast
            setFallbackTaskId(taskId);
            setShowFallbackDialog(true);
          } else {
            toast({
              title: "Generation Failed",
              description: errorMessage,
              variant: "destructive"
            });
          }
        }
      } catch (error) {
        clearInterval(interval);
        setPollingTaskIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(taskId);
          return newSet;
        });
        setIsEnhancing(false);
        toast({ title: "Connection Issue", description: "Unable to check progress. Please refresh and try again.", variant: "destructive" });
      }
    }, 3000);
  };

  const handleFormSubmit = async (data: ProductEnhancementFormData) => {
    // ALWAYS get fresh form values to ensure we use current state, not restored state
    const currentFormValues = getValues();
    const freshData = { ...data, ...currentFormValues };

    console.log('🔍 Form data comparison:', {
      passedData: data,
      currentFormValues: currentFormValues,
      freshData: freshData
    });
    // Immediately set submitting state to prevent flickering
    setIsSubmitting(true);

    // Validate multi-product requirements
    if (multiProductMode) {
      if (!selectedProducts || selectedProducts.length < 2) {
        setIsSubmitting(false);
        toast({
          title: "Add More Products",
          description: "Select at least 2 products to create a multi-product showcase. 📦",
          variant: "default"
        });
        return;
      }

      if (!data.product_arrangement) {
        // Auto-assign default arrangement based on product count
        const defaultArrangement = selectedProducts.length >= 3 ? 'adaptive-grid' : 'hero-supporting';
        setValue('product_arrangement', defaultArrangement);
        data.product_arrangement = defaultArrangement;
      }


    } else if (textOnlyMode) {
      // Service-based mode validation - textPrompt is now optional

      // Validate that a service-specific creative purpose is selected
      if (!data.adIntent) {
        setIsSubmitting(false);

        // Gentle highlighting instead of harsh toast
        setHighlightMissingFields(prev => ({ ...prev, creativePurpose: true }));

        // Auto-clear highlighting after 4 seconds
        setTimeout(() => {
          setHighlightMissingFields(prev => ({ ...prev, creativePurpose: false }));
        }, 4000);

        return;
      }



      // Validate event organization fields when event-organization is selected
      if (data.adIntent === 'event-organization') {
        if (!data.event_name || !data.event_name.trim()) {
          setIsSubmitting(false);
          toast({
            title: "Event Name Needed",
            description: "Please provide a name for your event. 📅",
            variant: "default"
          });
          return;
        }

        // Check if at least one additional detail is provided
        const hasEventDetails = data.event_date || data.event_location || data.event_type;
        if (!hasEventDetails) {
          setIsSubmitting(false);
          toast({
            title: "Add Event Details",
            description: "Please provide at least the event date, location, or type to create a complete event visual. ✨",
            variant: "default"
          });
          return;
        }
      }

      // Validate brand kit completion for service mode
      if (!useBrandify || !brandKit ||
        !brandKit.target_audience ||
        !brandKit.industry ||
        !brandKit.brand_personality ||
        brandKit.brand_personality.length === 0 ||
        !brandKit.primary_goal) {
        setIsSubmitting(false);
        toast({
          title: "Complete Your Brand Kit",
          description: "Service-based visuals work best with a complete brand kit. Please complete your brand information first. 🎨",
          variant: "default"
        });
        return;
      }

      // Helpful warning for Trust & Credibility without credibility images
      if (data.adIntent === 'trust-credibility' && selectedCredibilityImages.length === 0) {
        console.warn('⚠️ Trust & Credibility selected but no credibility images uploaded');
        toast({
          title: "💡 Tip: Add Credibility Images",
          description: "For best results with Trust & Credibility, upload certificates, awards, or testimonials to include in your ad.",
          variant: "default"
        });
      }

      // Validation for Self-Branding without self-image
      if (data.adIntent === 'self-branding' && selectedSelfImage.length === 0) {
        console.error('❌ Self-Branding selected but no self-image uploaded');
        setIsSubmitting(false);
        toast({
          title: "Self-Image Required",
          description: "Please upload a photo of yourself for Self-Branding visuals",
          variant: "destructive",
        });
        return;
      }
    } else if (!selectedImage || selectedImage.length === 0) {
      setIsSubmitting(false);
      toast({
        title: "Upload Product Image",
        description: "Please upload at least one product image to create your content. 📸",
        variant: "default"
      });
      return;
    }

    // AI tasks are credit-based only - no tier limit checks needed
    // Credits will be validated on the backend

    try {

      console.log('� Starrting new generation - using CURRENT form data only');

      console.log('🔍 Current form data will be used:', data);

      // ALWAYS clear persistence state and use current data when Generate is clicked
      clearPersistedState();

      // Clear old results IMMEDIATELY and SYNCHRONOUSLY to ensure progress shows
      setGeneratedEnhancedImages([]);
      setIsEnhancing(true);
      setHasActiveSession(true); // Mark as active session since user is generating
      setGenerationProgress(0);
      initialProgress.current = 0; // Reset initial progress for new generations
      setGenerationStartTime(Date.now());
      hasShownResumeToast.current = false;
      setCompletedTaskIds(new Set());
      // Clear persisted completed IDs (user-specific)
      if (currentUser?.id && currentClient?.id) {
        const userSpecificKey = `adStudio_completedTaskIds_${currentUser.id}_${currentClient.id}`;
        localStorage.removeItem(userSpecificKey);
      }
      // Also clear old non-user-specific key
      localStorage.removeItem('adStudio_completedTaskIds');
      setPollingTaskIds(new Set());

      console.log('✅ Generation will use current form state, ignoring any restored data');

      // Debug logging for generation mode
      if (textOnlyMode) {
        console.log('� Servdice-based generation mode!');
        console.log('Service Description:', textPrompt);
        console.log('Service Creative Purpose:', data.adIntent);
        console.log('Brand Kit Required - Complete:', {
          target_audience: !!brandKit?.target_audience,
          industry: !!brandKit?.industry,
          brand_personality: !!(brandKit?.brand_personality && brandKit.brand_personality.length > 0),
          primary_goal: !!brandKit?.primary_goal
        });
      } else {
        console.log('📦 Product-based generation mode');
        // Use ref value as fallback if React Hook Form doesn't have it
        const customDescription = data.prompt || customDescriptionRef.current?.value || '';
        console.log('Custom Description (data.prompt):', data.prompt);
        console.log('Custom Description from getValues:', getValues('prompt'));
        console.log('Custom Description from ref:', customDescriptionRef.current?.value);
        console.log('Final custom description to use:', customDescription);
        console.log('Multi-product mode:', multiProductMode);
        console.log('Show Advanced:', showAdvanced);
        console.log('Text Only Mode:', textOnlyMode);

        // Override data.prompt with the ref value if it exists
        if (customDescriptionRef.current?.value) {
          data.prompt = customDescriptionRef.current.value;
        }
      }

      // Debug logging for Brandify
      if (useBrandify) {
        console.log('🎨 Brandify enabled!');
        console.log('Brand Kit:', brandKit);
        console.log('Brand Voice:', brandKit?.brand_voice);
        console.log('Brand Colors:', brandKit?.brand_colors);
        console.log('Target Audience:', brandKit?.target_audience);
        console.log('Industry:', brandKit?.industry);
        console.log('Brand Personality:', brandKit?.brand_personality);
        console.log('Primary Goal:', brandKit?.primary_goal);
      } else {
        console.log('📝 Normal generation (Brandify disabled)');
      }

      // Debug logging for Reference Images
      if (data.reference_image_ids && data.reference_image_ids.length > 0) {
        console.log('🖼️ Reference images enabled!');
        console.log('Reference Image IDs:', data.reference_image_ids);
        console.log('Style Weight:', data.reference_style_weight);
        console.log('Composition Weight:', data.reference_composition_weight);
        console.log('Selected Reference Images:', selectedReferenceImages);
        console.log('Service Mode (textOnlyMode):', textOnlyMode);
      } else {
        console.log('📷 No reference images selected');
        console.log('Service Mode (textOnlyMode):', textOnlyMode);
        console.log('Reference image IDs in form data:', data.reference_image_ids);
        console.log('Selected reference images array:', selectedReferenceImages);
      }

      // Debug logging for generation mode
      if (textOnlyMode) {
        console.log('🏢 Service-based generation mode!');
        console.log('Service Description:', textPrompt);
        console.log('Service Creative Purpose:', data.adIntent);

        // Debug event organization data
        if (data.adIntent === 'event-organization') {
          console.log('📅 Event Organization Data:', {
            event_name: data.event_name,
            event_date: data.event_date,
            event_type: data.event_type,
            event_location: data.event_location
          });
        }

        // Debug credibility images
        if (data.adIntent === 'trust-credibility') {
          console.log('🏆 Trust & Credibility mode detected');
          console.log('Selected Credibility Images:', selectedCredibilityImages.length);
          console.log('Credibility Image Objects:', selectedCredibilityImages);
          console.log('Credibility Image IDs in form data:', data.credibility_image_ids);
          if (selectedCredibilityImages.length === 0) {
            console.warn('⚠️ No credibility images selected for Trust & Credibility mode');
          }
        }
      } else {
        console.log('📦 Product-based generation mode');
      }

      const requestBody = {
        // Handle service-based mode vs product-based mode
        ...(textOnlyMode && data.adIntent !== 'self-branding' ? {
          // Service-based mode: no input images, use text prompt or enhanced description
          description: (() => {
            let baseDescription = textPrompt.trim() || "Create professional advertisments for our service";
            const brandName = brandKit?.brand_name || currentClient?.name || 'the company';
            const industry = brandKit?.industry || 'business';

            // Enhanced description for event organization - avoid placeholders
            if (data.adIntent === 'event-organization' && data.event_name) {
              const eventDetails = `"${data.event_name}"${data.event_type ? ` (${data.event_type})` : ''}${data.event_date ? ` on ${data.event_date}` : ''}${data.event_location ? ` at ${data.event_location}` : ''}`;
              baseDescription = `Create a professional event advertisement for ${eventDetails} organized by ${brandName}. Focus on the event name, date, type, location, and organizer branding. DO NOT include placeholder text like [Your venue here], [Location], [Time], or [Contact info]. Use only the provided information: event name, date, type, location, and brand name. Create an elegant, professional design with clear typography and modern layout. ${baseDescription}`;
            }

            // Enhanced description for trust & credibility - avoid generating people and placeholders
            else if (data.adIntent === 'trust-credibility') {
              baseDescription = `Create a trust and credibility focused advertisement for ${brandName} in the ${industry} industry. Focus on certificates, awards, testimonials text, company logos, statistics, and professional branding elements. DO NOT include any human faces, people, or CEO photos. DO NOT use placeholder text like [Client name], [Company], or [Brand] - use the actual brand name "${brandName}". Use only text, logos, badges, and abstract professional imagery. ${baseDescription}`;
            }

            // General service-based enhancement - avoid all placeholders
            else {
              baseDescription = `Create a professional service advertisement for ${brandName} in the ${industry} industry. DO NOT include any placeholder text like [Your company], [Contact info], [Website], [Phone], [Email], [Address], or similar brackets. Use only the actual brand name "${brandName}" and industry "${industry}". Focus on professional design, clear messaging, and brand identity. ${baseDescription}`;
            }

            return baseDescription;
          })(),
          text_only_mode: true,
          service_mode: true, // Flag to indicate service-based generation
        } : data.adIntent === 'self-branding' ? {
          // Self-branding mode: use self-image as input
          input_media_file_id: data.self_image_id,
          description: textPrompt.trim() || "Create a professional self-branding advertisement that showcases expertise and builds trust",
          service_mode: true, // Flag to indicate service-based generation
        } : {
          // Product-based mode: single or multi-product support
          input_media_file_id: data.image_file_id,
          input_media_file_ids: data.image_file_ids,
          description: data.prompt || "Create a stunning advertisement that showcases the product with professional quality and visual appeal",
        }),
        // Only include creative fields if they have values (not empty/None)
        ...(data.adIntent && { ad_intent: data.adIntent }),
        ...(data.product_features && { product_features: data.product_features }),
        ...(data.product_features_title && { product_features_title: data.product_features_title }),
        ...(data.formatComposition && { format_composition: data.formatComposition }),
        ...(data.styleArtDirection && { style_art_direction: data.styleArtDirection }),
        ...(data.platformPlacement && { platform_placement: data.platformPlacement }),
        num_images: data.num_images,
        preserve_subject: true, // Always preserve subject - hidden from UI
        // Multi-product specific fields
        ...(data.product_arrangement && { product_arrangement: data.product_arrangement }),
        // Always send a default product_relationship for multi-product mode
        ...(data.image_file_ids && data.image_file_ids.length > 1 && { product_relationship: 'lifestyle-context' }),
        ...(data.product_priorities && { product_priorities: data.product_priorities }),
        ...(data.product_labels && { product_labels: data.product_labels }),
        ...(data.background_complexity && { background_complexity: data.background_complexity }),
        ...(data.color_harmony && { color_harmony: data.color_harmony }),
        // Reference image fields (for both product and service mode style guidance)
        ...(data.reference_image_ids && data.reference_image_ids.length > 0 && {
          reference_image_ids: data.reference_image_ids
        }),
        // Credibility image fields (for service mode - incorporated into final image)
        ...(data.credibility_image_ids && data.credibility_image_ids.length > 0 && textOnlyMode && {
          credibility_image_ids: data.credibility_image_ids
        }),
        ...(data.reference_style_weight && { reference_style_weight: data.reference_style_weight }),
        ...(data.reference_composition_weight && { reference_composition_weight: data.reference_composition_weight }),
        // Event organization fields
        ...(data.event_name && { event_name: data.event_name }),
        ...(data.event_date && { event_date: data.event_date }),
        ...(data.event_type && { event_type: data.event_type }),
        ...(data.event_location && { event_location: data.event_location }),
        // Self-branding fields
        ...(data.self_image_id && { self_image_id: data.self_image_id }),
        // Psychological influence tuning
        ...(data.influenceTuning && { psychological_levers: data.influenceTuning }),
        // Text & Overlay fields
        enable_text_overlay: data.enable_text_overlay,
        ...(data.enable_text_overlay && data.text_overlay_content && { text_overlay_content: data.text_overlay_content }),
        ...(data.enable_text_overlay && data.font_family && { font_family: data.font_family }),
        ...(data.enable_text_overlay && data.font_size && { font_size: data.font_size }),
        ...(data.enable_text_overlay && data.font_color && { font_color: data.font_color }),
        ...(data.enable_text_overlay && data.text_position && { text_position: data.text_position }),
        // Brandify integration
        use_brandify: useBrandify,
        ...(brandKit?.brand_name && { brand_name: brandKit.brand_name }),
        ...(brandKit?.brand_voice && { brand_voice: brandKit.brand_voice }),
        ...(brandKit?.website_url && { website_url: brandKit.website_url }),
        ...(brandKit?.brand_colors && { brand_colors: brandKit.brand_colors }),
        ...(brandKit?.logo_media_id && { logo_media_id: brandKit.logo_media_id }),
        // Enhanced brand kit fields
        ...(brandKit?.target_audience && { target_audience: brandKit.target_audience }),
        ...(brandKit?.industry && { industry: brandKit.industry }),
        ...(brandKit?.brand_personality && { brand_personality: brandKit.brand_personality }),
        ...(brandKit?.primary_goal && { primary_goal: brandKit.primary_goal }),
        // Image quality fields
        ...(data.aspect_ratio && { aspect_ratio: data.aspect_ratio }),
        ...(data.resolution && { resolution: data.resolution }),
        omit_text_overlay: data.omit_text_overlay ?? false,
        // AI Model selection
        ...(selectedModelId && { model_id: selectedModelId }),
      };

      console.log('🚀 API Request Body:', requestBody);

      // Specific debug for reference images in service mode
      if (textOnlyMode) {
        console.log('🔍 Service Mode Request Debug:');
        console.log('- Text only mode:', requestBody.text_only_mode);
        console.log('- Service mode flag:', requestBody.service_mode);
        console.log('- Reference image IDs:', requestBody.reference_image_ids);
        console.log('- Reference style weight:', requestBody.reference_style_weight);
        console.log('- Reference composition weight:', requestBody.reference_composition_weight);
        console.log('- Selected reference images count:', selectedReferenceImages.length);
      }

      // Specific debug for credibility images in request
      if (textOnlyMode && data.adIntent === 'trust-credibility') {
        console.log('🔍 Credibility Images Debug:');
        console.log('- Selected credibility images:', selectedCredibilityImages);
        console.log('- Form data credibility_image_ids:', data.credibility_image_ids);
        console.log('- Request includes credibility_image_ids:', !!requestBody.credibility_image_ids);
        console.log('- Credibility image IDs being sent:', requestBody.credibility_image_ids);
        console.log('- Service mode flag:', requestBody.service_mode);
        console.log('- Ad intent:', requestBody.ad_intent);
        console.log('- Text only mode:', textOnlyMode);
      }
      console.log('🔍 Form data debug:', {
        'data.image_file_id': data.image_file_id,
        'data.image_file_ids': data.image_file_ids,
        'selectedImage.length': selectedImage.length,
        'selectedProducts.length': selectedProducts.length,
        'textOnlyMode': textOnlyMode,
        'multiProductMode': multiProductMode,
      });

      const response = await fetch(`${getApiUrl()}/api/v1/ai/enhance-product`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient?.id || '',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        console.error('Error Detail Array:', errorData.detail);

        // Extract meaningful error message
        let errorMessage = 'Failed to create AI task';
        if (errorData.detail && Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => `${err.loc?.join('.')} - ${err.msg}`).join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }

        throw new Error(errorMessage);
      }
      const result = await response.json();

      // Save current state before starting generation (for potential navigation)
      saveStateForNavigation();

      if (result.task?.id) {
        // Add task to global context with comprehensive form data for restoration
        const comprehensiveFormData = {
          ...data,
          // Store mode states
          multiProductMode,
          textOnlyMode,
          useBrandify,
          showAdvanced,
          showInfluenceTuning,
          // Store text prompt
          textPrompt,
          // Store selected media with full objects for restoration
          selectedImage: selectedImage,
          selectedProducts: selectedProducts,
          selectedReferenceImages: selectedReferenceImages,
          // Store current form values
          formValues: getValues(),
          // Store timestamp for debugging
          submittedAt: new Date().toISOString(),
        };

        addTask({
          id: result.task.id,
          task_type: 'enhance-product',
          status: 'pending',
          created_at: new Date().toISOString(),
          page: 'ad-studio',
          formData: comprehensiveFormData,
        });

        pollTaskStatus(result.task.id);
      }
      toast({ title: "✨ Magic in Progress", description: "Crafting stunning content..." });
    } catch (error) {
      // Batch state update to prevent flickering
      React.startTransition(() => {
        setIsEnhancing(false);
        setIsSubmitting(false);
      });

      // Handle specific error cases
      if (error.message.includes('Insufficient credits')) {
        toast({
          title: "Insufficient Credits",
          description: "You don't have enough credits to use this feature. Please purchase more credits.",
          variant: "destructive"
        });
      } else if (error.message.includes('currently disabled')) {
        toast({
          title: "Feature Unavailable",
          description: "This feature is currently disabled. Please try again later.",
          variant: "destructive"
        });
      } else if (error.message.includes('No images generated') || error.message.includes('no images generated')) {
        toast({
          title: "Generation Failed",
          description: "Image generation failed due to parameter restrictions. No credits were deducted. Please adjust your settings and try again.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Generation Error",
          description: "Unable to process your request. Please try different parameters or contact support if the issue persists.",
          variant: "destructive"
        });
      }
    }
  };

  const startNewGeneration = () => {
    // Clear ALL cached tasks for ad-studio to prevent any restoration conflicts
    const allTasks = getTasksByPage('ad-studio');
    allTasks.forEach(task => {
      console.log('🗑️ Removing cached task:', task.id);
      removeTask(task.id);
    });

    // Clear all generated results and reset form
    setGeneratedEnhancedImages([]);
    setStartFresh(true);

    // Clear selected images and products
    setSelectedImage([]);
    setSelectedProducts([]);
    setSelectedReferenceImages([]);

    // Force re-render of MultiMediaFileSelector components only when necessary
    console.log('🔄 Forcing re-render of media selectors');
    setSelectorKey(prev => prev + 1);

    // Only reset form when explicitly requested by user
    console.log('🔄 User requested fresh start - resetting form completely');
    reset();

    // Explicitly clear image file IDs to ensure they're not cached
    setValue('image_file_id', undefined);
    setValue('image_file_ids', undefined);
    setValue('reference_image_ids', undefined);

    // Reset other state
    setMultiProductMode(false);
    setTextOnlyMode(false);
    setTextPrompt('');
    setShowAdvanced(false);
    setShowInfluenceTuning(false);
    setUseBrandify(false);
    setIsEnhancing(false);
    setIsSubmitting(false);

    console.log('🔄 Ad Studio reset completed - all cached tasks removed and state cleared');
  };

  const handleDownload = async (imageUrl?: string, index?: number) => {
    const imagesToDownload = imageUrl ? [imageUrl] : generatedEnhancedImages;
    console.log('🔍 Download Debug:', {
      imageUrl,
      index,
      generatedEnhancedImages,
      imagesToDownload,
      totalImages: generatedEnhancedImages.length
    });

    if (imagesToDownload.length === 0 || !currentClient) return;

    try {

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'X-Client-ID': currentClient.id,
      };

      // Download each image
      console.log(`🔍 Starting download of ${imagesToDownload.length} images`);
      for (let i = 0; i < imagesToDownload.length; i++) {
        const currentImageUrl = imagesToDownload[i];
        console.log(`🔍 Downloading image ${i + 1}/${imagesToDownload.length}:`, currentImageUrl);
        const isAbsoluteUrl = currentImageUrl.startsWith('http');
        const fullImageUrl = isAbsoluteUrl ? currentImageUrl : `${getApiUrl()}${currentImageUrl}`;

        // For external URLs (like S3 presigned), we might not need auth headers.
        const fetchOptions = isAbsoluteUrl ? {} : { headers };

        const response = await fetch(fullImageUrl, fetchOptions);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const suffix = imagesToDownload.length > 1 ? `_${i + 1}` : '';
        link.download = `enhanced-ad${suffix}-${Date.now()}.png`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      toast({
        title: "📥 Download Complete",
        description: `${imagesToDownload.length} stunning advertisement${imagesToDownload.length > 1 ? 's have' : ' has'} been saved to your device!`
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: "Download Failed",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleCreatePost = async (imageUrl?: string) => {
    const imageToUse = imageUrl || generatedEnhancedImages[0];
    if (!imageToUse || !currentClient) return;

    try {
      toast({ title: "📝 Creating Post", description: "Adding your masterpiece to the post editor..." });

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'X-Client-ID': currentClient.id,
      };

      const isAbsoluteUrl = imageToUse.startsWith('http');
      const fullImageUrl = isAbsoluteUrl ? imageToUse : `${getApiUrl()}${imageToUse}`;

      // For external URLs (like S3 presigned), we might not need auth headers.
      // This logic assumes internal URLs need auth, external ones don't.
      const fetchOptions = isAbsoluteUrl ? {} : { headers };

      // Fetch the generated image
      const response = await fetch(fullImageUrl, fetchOptions);

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      // Create a File object from the blob
      const fileName = `enhanced-ad-${Date.now()}.png`;
      const file = new File([blob], fileName, {
        type: blob.type || 'image/png',
        lastModified: Date.now()
      });

      console.log('Created file:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });

      // Upload the file to media library with metadata
      console.log('Uploading file:', file.name, file.size, file.type);
      console.log('Current client:', currentClient);

      // Create FormData with proper metadata
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', 'AI Enhanced Advertisement');
      formData.append('description', 'Generated by Ad Studio AI');
      formData.append('tags', 'ai-generated,advertisement,enhanced');

      const uploadApiResponse = await fetch(`${getApiUrl()}/api/v1/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
        body: formData,
      });

      if (!uploadApiResponse.ok) {
        const errorData = await uploadApiResponse.text();
        console.error('Upload failed:', uploadApiResponse.status, errorData);
        throw new Error(`Upload failed: ${uploadApiResponse.status} ${uploadApiResponse.statusText}`);
      }

      const uploadResponse = await uploadApiResponse.json();
      console.log('Upload response:', uploadResponse);

      // Check if upload was successful
      if (!uploadResponse) {
        throw new Error('Upload failed: No response from server');
      }

      if (!uploadResponse.file) {
        console.error('Upload response missing file:', uploadResponse);
        throw new Error('Upload failed: Server response missing file data');
      }

      if (!uploadResponse.file.id) {
        console.error('File missing ID:', uploadResponse.file);
        throw new Error('Upload failed: File missing ID');
      }

      // Navigate to post creation with the uploaded media ID
      navigate('/app/posts/new', {
        state: {
          selectedMediaIds: [uploadResponse.file.id],
          generatedFromAdStudio: true
        }
      });

      toast({
        title: "🚀 Ready to Post",
        description: "Your masterpiece has been added to the post editor. Time to share it with the world!"
      });
    } catch (error) {
      console.error('Failed to create post:', error);
      toast({
        title: "Failed to Create Post",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    }
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background text-foreground">
        {/* Floating Header */}
        <div className="backdrop-blur-2xl bg-background/60 border-b border-border/30">
          <div className="px-4 sm:px-6 py-3">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary via-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-primary/25">
                    <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">
                      Image Studio
                    </h1>
                    <p className="hidden sm:block text-muted-foreground">AI-Powered Image Creation</p>
                  </div>

                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    performNuclearReset();
                  }}
                  className="px-3 py-2 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-900/30 border border-red-200 dark:border-red-800 hover:from-red-100 hover:to-red-200 dark:hover:from-red-900/30 dark:hover:to-red-900/40 text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200 transition-all duration-200 rounded text-sm flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="hidden sm:inline font-medium">Reset</span>
                </button>
                {!isMobile && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      restartTour(adStudioSteps, 'adStudio');
                    }}
                    className="px-3 py-2 bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 border border-pink-200 dark:border-pink-800 hover:from-pink-100 hover:to-purple-100 dark:hover:from-pink-900/30 dark:hover:to-purple-900/30 text-pink-700 dark:text-pink-300 hover:text-pink-800 dark:hover:text-pink-200 transition-all duration-200 rounded text-sm flex items-center gap-2"
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span className="hidden sm:inline font-medium">Tour</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>




        {/* Main Content */}
        <div className="px-2 sm:px-4 lg:px-6 py-2 sm:py-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-6">

            {/* Left Panel - Controls */}
            <div className="lg:col-span-4 space-y-1 sm:space-y-2 flex flex-col relative">

              {/* Refresh Square - Above Form Border */}
              {!isMobile && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      onClick={performNuclearReset}
                      variant="outline"
                      size="sm"
                      className="absolute -top-2 right-0 h-8 w-8 p-0 bg-background border border-border hover:border-primary/50 hover:bg-primary/5 transition-all duration-200 group z-10 rounded-lg shadow-md"
                    >
                      <RotateCcw className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reset all - clears all states including stuck tasks</p>
                  </TooltipContent>
                </Tooltip>
              )}

              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                onClick={clearFieldHighlights}
                className="ad-studio-form space-y-2 flex-1 min-h-[400px] lg:min-h-[600px] flex flex-col"
              >

                {/* Form Content - Flex Grow */}
                <div className="flex-1 space-y-2">

                  {/* Upload Section */}
                  <div id="upload-section" className="group relative overflow-visible rounded-xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border-border/30 shadow-lg shadow-primary/5">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>
                    <div className="relative p-3">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-7 h-7 bg-gradient-to-br from-primary via-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                          {textOnlyMode ? (
                            <Type className="w-4 h-4 text-primary-foreground" />
                          ) : (
                            <ImageIcon className="w-4 h-4 text-primary-foreground" />
                          )}
                        </div>
                        <h3 className="text-base font-bold text-card-foreground">
                          {textOnlyMode ? 'Service Description' : 'Product Upload'}
                        </h3>
                      </div>

                      <div className="space-y-2">
                        {/* Creation Mode Toggle - Compact Tab Row */}
                        <div className="space-y-2">
                          <div className="text-muted-foreground font-medium mb-1 block" style={{ fontSize: '14px' }}>
                            Creation Mode
                          </div>

                          <div className="flex items-center justify-center space-x-1 bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-1 shadow-inner border border-slate-300/50 dark:border-slate-600/30">
                            <button
                              type="button"
                              onClick={() => {
                                if (textOnlyMode) {
                                  setTextOnlyMode(false);
                                  setTextPrompt('');
                                  setSelectedCredibilityImages([]);
                                  setUseBrandify(false);
                                  // Clear service-based creative purpose when switching to product-based
                                  setValue('adIntent', '');
                                }
                              }}
                              className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${!textOnlyMode
                                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xl shadow-indigo-500/10 dark:shadow-indigo-500/20 border border-indigo-200/50 dark:border-indigo-700/50'
                                : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-800/50'
                                }`}
                            >
                              <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all duration-300 ${!textOnlyMode
                                ? 'bg-gradient-to-br from-primary via-purple-500 to-pink-500 text-white shadow-lg shadow-pink-500/30'
                                : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                                }`}>
                                <ImageIcon className="w-3 h-3" />
                              </div>
                              <div className="text-left">
                                <div className="font-semibold text-xs">Product-Based</div>
                                <div className={`text-xs ${!textOnlyMode
                                  ? 'text-slate-600 dark:text-slate-400'
                                  : 'text-slate-500 dark:text-slate-500'
                                  }`}>
                                  Upload images
                                </div>
                              </div>
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                if (!textOnlyMode) {
                                  setTextOnlyMode(true);
                                  setSelectedImage([]);
                                  setSelectedProducts([]);
                                  setSelectedReferenceImages([]);
                                  setSelectedCredibilityImages([]);
                                  setMultiProductMode(false);
                                  setUseBrandify(true);
                                  // Clear product-based creative purpose when switching to service-based
                                  setValue('adIntent', '');
                                }
                              }}
                              className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${textOnlyMode
                                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xl shadow-indigo-500/10 dark:shadow-indigo-500/20 border border-indigo-200/50 dark:border-indigo-700/50'
                                : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-800/50'
                                }`}
                            >
                              <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all duration-300 ${textOnlyMode
                                ? 'bg-gradient-to-br from-primary via-purple-500 to-pink-500 text-white shadow-lg shadow-pink-500/30'
                                : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                                }`}>
                                <Type className="w-3 h-3" />
                              </div>
                              <div className="text-left">
                                <div className="font-semibold text-xs">Service-Based</div>
                                <div className={`text-xs ${textOnlyMode
                                  ? 'text-slate-600 dark:text-slate-400'
                                  : 'text-slate-500 dark:text-slate-500'
                                  }`}>
                                  Use brand identity
                                </div>
                              </div>
                            </button>
                          </div>

                          {/* Compact Mode Description */}
                        </div>

                        {textOnlyMode ? (
                          /* Service Description Input */
                          <div id="text-prompt-input">
                            <div className="text-muted-foreground font-medium mb-2 block" style={{ fontSize: '14px' }}>
                              Additional details (Optional)
                            </div>
                            <div className="relative">
                              <textarea
                                ref={textPromptRef}
                                value={textPrompt}
                                onChange={(e) => {
                                  setTextPrompt(e.target.value);
                                  autoResizeTextarea(e.target);
                                }}
                                placeholder="e.g., 'Include our 24/7 support feature' or 'Mention our free consultation offer' or 'Focus on our 10+ years experience'"
                                className="w-full min-h-[80px] p-2 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 resize-none overflow-hidden"
                                maxLength={1000}
                                style={{ fontSize: '14px', minHeight: '80px' }}
                              />
                              <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
                                {textPrompt.length}/1000
                              </div>
                            </div>


                          </div>
                        ) : (
                          <div id="product-image-upload">
                            <div className="text-muted-foreground font-medium mb-2 block" style={{ fontSize: '14px' }}>
                              {multiProductMode ? 'Product Images (2-6)' : 'Product Images'}
                            </div>

                            {multiProductMode ? (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-muted-foreground">
                                    {selectedProducts.length}/6 selected
                                  </span>
                                  {selectedProducts.length >= 2 && (
                                    <span className="text-xs text-green-600 dark:text-green-400">
                                      ✓ Ready for multi-product
                                    </span>
                                  )}
                                </div>
                                <MultiMediaFileSelector selectedFiles={selectedProducts}
                                  onSelectionChange={(files) => {
                                    const limitedFiles = files.slice(0, 6);

                                    if (files.length > 6) {
                                      toast({
                                        title: "Selection Limit",
                                        description: "Maximum 6 products allowed for multi-product visuals. First 6 selected.",
                                        variant: "destructive",
                                        duration: 3000,
                                      });
                                    }

                                    if (limitedFiles.length < 2) {
                                      setMultiProductMode(false);
                                      setSelectedImage(limitedFiles);
                                      setSelectedProducts([]);
                                    } else {
                                      setSelectedProducts(limitedFiles);
                                    }
                                  }}
                                  acceptedTypes={['image']}
                                />
                              </div>
                            ) : (
                              <div className="relative group">
                                <MultiMediaFileSelector
                                  selectedFiles={selectedImage}
                                  onSelectionChange={(files) => {
                                    setSelectedImage(files);
                                  }}
                                  onDone={(files) => {
                                    // Auto-enable multi-product mode when Done is clicked with multiple images
                                    if (!multiProductMode && files.length > 1) {
                                      const limitedFiles = files.slice(0, 6);
                                      setMultiProductMode(true);
                                      setSelectedProducts(limitedFiles);
                                      setSelectedImage([]);

                                      toast({
                                        title: "🎯 Multi-Product Mode Activated",
                                        description: `Ready to showcase ${limitedFiles.length} products together. Configure your layout below.`,
                                        duration: 3000,
                                      });

                                      // Show warning if user selected more than 6
                                      if (files.length > 6) {
                                        toast({
                                          title: "Selection Limit",
                                          description: "Maximum 6 products allowed. First 6 selected.",
                                          variant: "destructive",
                                          duration: 3000,
                                        });
                                      }
                                    }
                                  }}
                                  acceptedTypes={['image']}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Reference Images Section - Show in both product and service modes */}
                        {(
                          <div id="reference-images-section" className="space-y-3">
                            {/* Collapsible Header */}
                            <div className="flex items-center justify-between p-2 bg-background/50 rounded-lg border border-border/30">
                              <div className="flex items-center space-x-2">
                                <ImageIcon className="w-4 h-4 text-muted-foreground" />
                                <div className="font-normal" style={{ fontSize: '14px' }}>
                                  Reference Images (Optional)
                                </div>
                              </div>
                              <Switch
                                checked={showReferenceImages}
                                onCheckedChange={setShowReferenceImages}
                              />
                            </div>

                            {/* Collapsible Content */}
                            {showReferenceImages && (
                              <div className="space-y-3 animate-in slide-in-from-top-2 duration-300">
                                <MultiMediaFileSelector
                                  selectedFiles={selectedReferenceImages}
                                  onSelectionChange={setSelectedReferenceImages}
                                  acceptedTypes={['image']}
                                />

                                {/* Reference Weight Controls */}
                                {selectedReferenceImages.length > 0 && (
                                  <div className="space-y-2 p-3 bg-background/30 rounded-lg border border-border/50">
                                    <div>
                                      <Label className="text-xs text-muted-foreground font-medium mb-1 block">
                                        Style Influence: {watch('reference_style_weight', 0.7)}
                                      </Label>
                                      <Controller
                                        name="reference_style_weight"
                                        control={control}
                                        render={({ field }) => (
                                          <Slider
                                            value={[field.value || 0.7]}
                                            onValueChange={(value) => field.onChange(value[0])}
                                            min={0.1}
                                            max={1.0}
                                            step={0.1}
                                            className="w-full"
                                          />
                                        )}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground font-medium mb-1 block">
                                        Composition Influence: {watch('reference_composition_weight', 0.5)}
                                      </Label>
                                      <Controller
                                        name="reference_composition_weight"
                                        control={control}
                                        render={({ field }) => (
                                          <Slider
                                            value={[field.value || 0.5]}
                                            onValueChange={(value) => field.onChange(value[0])}
                                            min={0.1}
                                            max={1.0}
                                            step={0.1}
                                            className="w-full"
                                          />
                                        )}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Multi-Product Mode Toggle - Hide in text-only mode */}
                        {!textOnlyMode && (
                          <div id="multi-product-toggle" className="mt-3 pt-3 border-t border-border/50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                                  <Layers className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                  <span className="text-base font-semibold text-card-foreground">Multi-Product Mode</span>
                                  <p className="text-sm text-muted-foreground">Combine 2-6 product images in one generation</p>
                                </div>
                              </div>
                              <Switch
                                checked={multiProductMode}
                                onCheckedChange={(checked) => {
                                  setMultiProductMode(checked);
                                  // Clear before/after selection if switching to multi-product mode
                                  if (checked && watch('adIntent') === 'before-after') {
                                    setValue('adIntent', '');
                                  }
                                }}
                                className="data-[state=checked]:bg-purple-500"
                              />
                            </div>

                            {/* Multi-Product Settings */}
                            {multiProductMode && selectedProducts.length >= 2 && (
                              <div className="mt-3 space-y-3">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Layers className="w-4 h-4 text-muted-foreground" />
                                  <span className="text-sm font-medium text-card-foreground">Multi-Product Settings</span>
                                </div>

                                <div>
                                  <Controller
                                    name="product_arrangement"
                                    control={control}
                                    rules={{ required: false }}
                                    render={({ field }) => (
                                      <ThumbnailSelect
                                        value={field.value}
                                        onChange={field.onChange}
                                        options={getArrangementOptions(selectedProducts.length)}
                                        placeholder="Arrangement"
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                            )}

                          </div>
                        )}

                      </div>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="border-t border-border/50"></div>

                  {/* Style Selection */}
                  <div id="creative-style-section" className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border-border/30 shadow-lg shadow-purple-500/5">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-rose-500/5"></div>
                    <div className="relative p-3">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                          <Palette className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-base font-bold text-card-foreground">Creative Style</h3>
                      </div>

                      <div id="creative-style-controls" className="grid grid-cols-1 lg:grid-cols-3 gap-2">
                        <div className={`relative ${highlightMissingFields.creativePurpose ? 'field-highlight-animation' : ''}`}>
                          {highlightMissingFields.creativePurpose && (
                            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl border border-emerald-400/30 z-10 pointer-events-none animate-pulse">
                              <div className="absolute -top-7 right-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs px-3 py-1 rounded-full shadow-lg whitespace-nowrap">
                                Please select a purpose
                              </div>
                            </div>
                          )}
                          <Controller
                            name="adIntent"
                            control={control}
                            render={({ field }) => (
                              <ThumbnailSelect
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                  // Clear highlighting when user selects
                                  if (value && highlightMissingFields.creativePurpose) {
                                    setHighlightMissingFields({ creativePurpose: false });
                                  }
                                }}
                                options={getCreativePurposeOptions(multiProductMode, textOnlyMode)}
                                placeholder="Purpose"
                              />
                            )}
                          />
                        </div>

                        {/* Credibility Images - Only show when Trust & Credibility is selected in service mode */}
                        {textOnlyMode && watchedAdIntent === 'trust-credibility' && (
                          <div className="space-y-2 lg:col-span-3">
                            <div className="text-muted-foreground font-medium mb-2 block" style={{ fontSize: '14px' }}>
                              Credibility Images (Optional)
                            </div>
                            <MultiMediaFileSelector
                              selectedFiles={selectedCredibilityImages}
                              onSelectionChange={setSelectedCredibilityImages}
                              acceptedTypes={['image']}
                            />
                            <p className="text-xs text-muted-foreground">
                              Upload certificates, awards, testimonials, or other credibility documents to incorporate into your ad
                            </p>
                          </div>
                        )}

                        {/* Self Image - Only show when Self-Branding is selected in service mode */}
                        {textOnlyMode && watchedAdIntent === 'self-branding' && (
                          <div className="space-y-2 lg:col-span-3">
                            <div className="text-muted-foreground font-medium mb-2 block" style={{ fontSize: '14px' }}>
                              Your Photo (Required)
                            </div>
                            <MultiMediaFileSelector
                              selectedFiles={selectedSelfImage}
                              onSelectionChange={setSelectedSelfImage}
                              acceptedTypes={['image']}
                              maxFiles={1}
                            />
                            <p className="text-xs text-muted-foreground">
                              Upload a photo of yourself to be featured in the content
                            </p>
                          </div>
                        )}

                        {/* Product Features Fields - Only show when Product Features is selected and not in service mode */}
                        {watchedAdIntent === 'product-features' && !textOnlyMode && (
                          <div className="lg:col-span-3">
                            <style jsx>{`
                              .product-features-input::placeholder,
                              .product-features-textarea::placeholder {
                                font-size: 14px !important;
                              }
                              .product-features-input,
                              .product-features-textarea {
                                font-size: 14px !important;
                              }
                            `}</style>
                            <div className="space-y-4">
                              {/* Title Field (Optional) */}
                              <div className="space-y-2">
                                <Label htmlFor="product_features_title" className="text-sm font-medium text-card-foreground">
                                  Title <span className="text-xs text-muted-foreground">(Optional)</span>
                                </Label>
                                <input
                                  {...register('product_features_title')}
                                  id="product_features_title"
                                  type="text"
                                  placeholder="e.g., 'Thoughtfully designed features'"
                                  className="product-features-input w-full px-3 py-2 text-xs border border-border rounded-md bg-background text-card-foreground placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-xs text-muted-foreground">
                                  Optional title that will appear above the product features. Leave empty for no title.
                                </p>
                              </div>

                              {/* Product Features Field (Mandatory) */}
                              <div className="space-y-2">
                                <Label htmlFor="product_features" className="text-sm font-medium text-card-foreground">
                                  Product Features & Specifications <span className="text-red-500">*</span>
                                </Label>
                                <textarea
                                  {...register('product_features', {
                                    required: 'Product features are required when Product Features is selected',
                                    onChange: (e) => autoResizeTextarea(e.target)
                                  })}
                                  id="product_features"
                                  placeholder="Key features to highlight (e.g., 'Water-resistant, 5 pockets, Lifetime warranty')"
                                  className="product-features-textarea w-full min-h-[60px] px-3 py-2 text-xs border border-border rounded-md bg-background text-card-foreground placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                                  rows={2}
                                />
                                {errors.product_features && (
                                  <p className="text-xs text-red-500">{errors.product_features.message}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  List the key features, specifications, and benefits you want to showcase with callouts and overlays
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Event Organization Fields - Only show when Event Organization is selected in service mode */}
                        {textOnlyMode && watchedAdIntent === 'event-organization' && (
                          <div className="space-y-3 p-3 bg-background/50 rounded-lg border border-border/30 lg:col-span-3">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              <span className="text-sm font-medium text-muted-foreground">Event Details</span>
                            </div>

                            {/* Event Name */}
                            <div className="space-y-1">
                              <Label htmlFor="event_name" className="text-xs text-muted-foreground">
                                Event Name <span className="text-red-500">*</span>
                              </Label>
                              <input
                                {...register('event_name', {
                                  required: watchedAdIntent === 'event-organization' ? 'Event name is required' : false,
                                  validate: watchedAdIntent === 'event-organization' ? (value) => {
                                    if (!value || !value.trim()) {
                                      return 'Event name cannot be empty';
                                    }
                                    return true;
                                  } : undefined
                                })}
                                id="event_name"
                                type="text"
                                placeholder="e.g., 'Digital Marketing Masterclass'"
                                className="w-full h-9 px-3 border border-border rounded-md bg-background text-card-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                                style={{
                                  fontSize: '12px'
                                }}
                              />
                              {errors.event_name && (
                                <p className="text-xs text-red-500">{errors.event_name.message}</p>
                              )}
                            </div>

                            {/* Event Location */}
                            <div className="space-y-1">
                              <Label htmlFor="event_location" className="text-xs text-muted-foreground">
                                Location/Venue <span className="text-orange-500">*</span>
                              </Label>
                              <input
                                {...register('event_location')}
                                id="event_location"
                                type="text"
                                placeholder="e.g., 'Downtown Conference Center' or 'Online'"
                                className="w-full h-9 px-3 border border-border rounded-md bg-background text-card-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                                style={{
                                  fontSize: '12px'
                                }}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              {/* Event Type */}
                              <div className="space-y-1">
                                <Label htmlFor="event_type" className="text-xs text-muted-foreground">
                                  Type <span className="text-orange-500">*</span>
                                </Label>
                                <Controller
                                  name="event_type"
                                  control={control}
                                  render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                      <SelectTrigger className="w-full h-9 text-sm">
                                        <SelectValue placeholder="Select type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {eventTypeOptions.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                />
                              </div>

                              {/* Event Date */}
                              <div className="space-y-1">
                                <Label htmlFor="event_date" className="text-xs text-muted-foreground">
                                  Date <span className="text-orange-500">*</span>
                                </Label>
                                <input
                                  {...register('event_date')}
                                  id="event_date"
                                  type="date"
                                  className="w-full h-9 px-3 border border-border rounded-md bg-background text-card-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                                  style={{
                                    fontSize: '12px'
                                  }}
                                />
                              </div>
                            </div>

                            <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                              <p className="text-xs text-orange-700 dark:text-orange-300">
                                <span className="font-medium">Required:</span> Event name + at least one detail (date, location, or type) for a complete event content.
                              </p>
                            </div>
                          </div>
                        )}

                        {!multiProductMode && !textOnlyMode && (
                          <div>
                            <Controller
                              name="formatComposition"
                              control={control}
                              render={({ field }) => (
                                <ThumbnailSelect
                                  value={field.value}
                                  onChange={field.onChange}
                                  options={singleProductCompositionOptions}
                                  placeholder="Composition"
                                />
                              )}
                            />
                          </div>
                        )}

                        {/* Visual Style - Only show when no reference images are selected */}
                        {selectedReferenceImages.length === 0 && (
                          <div>
                            <Controller
                              name="styleArtDirection"
                              control={control}
                              render={({ field }) => {
                                // Create display options with illustration sub-style replacement
                                let displayOptions = [...visualStyleOptions];

                                // Check if an illustration sub-style is selected
                                const selectedIllustrationStyle = illustrationStyleOptions.find(
                                  style => style.value === field.value
                                );

                                if (selectedIllustrationStyle) {
                                  // Replace the "illustration" option with the selected sub-style
                                  displayOptions = displayOptions.map(option =>
                                    option.value === 'illustration'
                                      ? {
                                        value: selectedIllustrationStyle.value,
                                        label: `Illustration:\n${selectedIllustrationStyle.label}`,
                                        thumbnail: selectedIllustrationStyle.thumbnail
                                      }
                                      : option
                                  );
                                }

                                return (
                                  <ThumbnailSelect
                                    value={field.value}
                                    onChange={(value) => {
                                      if (value === 'illustration') {
                                        setShowIllustrationModal(true);
                                      } else {
                                        field.onChange(value);
                                      }
                                    }}
                                    options={displayOptions}
                                    placeholder="Visual Style"
                                    compact={true}
                                  />
                                );
                              }}
                            />
                          </div>
                        )}

                        {/* Reference Images Style Override Notice */}
                        {selectedReferenceImages.length > 0 && (
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <p className="text-xs text-blue-700 dark:text-blue-300">
                                Visual style will be guided by your reference images
                              </p>
                            </div>
                          </div>
                        )}
                      </div>





                      {/* Brandify Toggle */}
                      <div id="brandify-toggle" className="mt-3 pt-3 border-t border-border/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                              <Target className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="text-base font-semibold text-card-foreground">Brandify</span>
                                {(currentUser?.tier?.name?.toLowerCase() === 'free' && !currentUser?.is_beta_tester) && (
                                  <Crown className="w-3 h-3 text-amber-500" />
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {textOnlyMode ? 'Required for service-based visuals' : 'Apply brand identity to your generation'}
                              </p>
                            </div>
                          </div>
                          <Switch
                            checked={useBrandify}
                            onCheckedChange={textOnlyMode ? undefined : setUseBrandify}
                            disabled={textOnlyMode}
                            className="data-[state=checked]:bg-emerald-500"
                          />
                        </div>

                        {/* Free user upgrade message */}
                        {(currentUser?.tier?.name?.toLowerCase() === 'free' && !currentUser?.is_beta_tester && !textOnlyMode && useBrandify) && (
                          <div className="mt-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                            <div className="flex items-start space-x-2">
                              <Crown className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">
                                  Upgrade to use Brandify for no extra credits
                                </p>
                                <p className="text-xs text-amber-700 dark:text-amber-300">
                                  Paid plans include Brandify at no additional cost. Apply your brand colors and logo automatically to every image.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Brand Kit Completion Hint */}
                        {useBrandify && brandKit && (
                          !brandKit.target_audience ||
                          !brandKit.industry ||
                          !brandKit.brand_personality ||
                          brandKit.brand_personality.length === 0 ||
                          !brandKit.primary_goal
                        ) && (
                            <div className={`mt-3 p-3 rounded-lg border ${textOnlyMode
                              ? 'bg-orange-50/50 dark:bg-orange-950/20 border-orange-200/50 dark:border-orange-800/30'
                              : 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-200/50 dark:border-blue-800/30'
                              }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${textOnlyMode
                                    ? 'bg-orange-500'
                                    : 'bg-blue-500'
                                    }`}>
                                    <span className="text-white text-xs">{textOnlyMode ? '⚠️' : '💡'}</span>
                                  </div>
                                  <span className={`text-xs font-medium ${textOnlyMode
                                    ? 'text-orange-700 dark:text-orange-300'
                                    : 'text-blue-700 dark:text-blue-300'
                                    }`}>
                                    {textOnlyMode ? 'Brand kit completion required for service visuals' : 'Complete your brand kit for better results'}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/app/clients/${currentClient?.id}/brand-kit`)}
                                  className={`h-6 px-2 text-xs ${textOnlyMode
                                    ? 'text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30'
                                    : 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                                    }`}
                                >
                                  Setup →
                                </Button>
                              </div>
                            </div>
                          )}

                        {useBrandify && brandKit && (
                          <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg border border-emerald-200 dark:border-emerald-800/30">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs">✓</span>
                              </div>
                              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">Brand Kit Active</span>
                            </div>
                            <div className="text-xs text-emerald-700 dark:text-emerald-300 space-y-1">
                              {brandKit.brand_name && <p>• Brand Name: {brandKit.brand_name}</p>}
                              {brandKit.brand_voice && <p>• Brand Voice: {brandKit.brand_voice.slice(0, 50)}...</p>}
                              {brandKit.website_url && brandKit.website_url !== 'https://example.com' && <p>• Website: {brandKit.website_url}</p>}
                              {brandKit.target_audience && <p>• Target Audience: {brandKit.target_audience}</p>}
                              {brandKit.industry && <p>• Industry: {brandKit.industry}</p>}
                              {brandKit.brand_personality && brandKit.brand_personality.length > 0 && (
                                <p>• Personality: {brandKit.brand_personality.slice(0, 3).join(', ')}{brandKit.brand_personality.length > 3 ? '...' : ''}</p>
                              )}
                              {brandKit.primary_goal && <p>• Goal: {brandKit.primary_goal}</p>}
                              <p>• Logo integration: {brandKit.logo_media_id ? 'Available' : 'Not set'}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Output Settings moved to bottom next to Generate button */}

                      {/* Advanced Toggle */}
                      <div className="mt-2 pt-2 border-t border-border/50">
                        <div className="flex items-center justify-between p-2 bg-background/50 rounded-lg border border-border/30">
                          <div className="flex items-center space-x-2">
                            <Settings className="w-4 h-4 text-muted-foreground" />
                            <div className="font-normal" style={{ fontSize: '14px' }}>Advanced Options</div>
                          </div>
                          <Switch
                            checked={showAdvanced}
                            onCheckedChange={setShowAdvanced}
                          />
                        </div>

                        {showAdvanced && (
                          <div className="mt-2 space-y-2 animate-in slide-in-from-top-2 duration-300">

                            {/* Creative Vision Section - Only show in product-based mode */}
                            {!textOnlyMode && (
                              <div className="space-y-2 pt-2 border-t border-border/50">
                                <h4 className="text-sm font-bold text-card-foreground flex items-center">
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  Creative Vision
                                </h4>

                                <div>
                                  <Label className="text-xs text-muted-foreground mb-1 block">Custom Description (Optional)</Label>
                                  <div className="relative">
                                    <textarea
                                      {...register('prompt', {
                                        onChange: (e) => {
                                          autoResizeTextarea(e.target);
                                          // Also update the ref to ensure we have the value
                                          if (customDescriptionRef.current) {
                                            customDescriptionRef.current.value = e.target.value;
                                          }
                                        }
                                      })}
                                      ref={customDescriptionRef}
                                      className="w-full min-h-[60px] p-2 bg-background/50 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 resize-none overflow-hidden"
                                      placeholder="Brief creative description..."
                                      style={{ fontSize: '14px', minHeight: '60px', color: 'inherit' }}
                                    />
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">Leave empty to use AI's creative interpretation based on your settings</p>
                                </div>
                              </div>
                            )}

                            {/* Text & Overlay Section */}
                            <div className="space-y-2 pt-2 border-t border-border/50">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-bold text-card-foreground flex items-center">
                                  <Type className="w-4 h-4 mr-2" />
                                  Text & Overlay
                                </h4>
                                <Controller
                                  name="enable_text_overlay"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                                  )}
                                />
                              </div>

                              {watchedEnableTextOverlay && (
                                <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                                  <div>
                                    <Label className="text-xs text-muted-foreground mb-1 block">Overlay Text</Label>
                                    <textarea
                                      {...register('text_overlay_content', {
                                        onChange: (e) => autoResizeTextarea(e.target)
                                      })}
                                      rows={2}
                                      className="w-full px-3 py-2 bg-background/50 border-0 rounded-lg focus:ring-2 focus:ring-primary/50 text-sm resize-none"
                                      placeholder="e.g., Limited Time Offer!"
                                    />
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                    <div>
                                      <Label className="text-xs text-muted-foreground mb-1 block">Font Family</Label>
                                      <Controller
                                        name="font_family"
                                        control={control}
                                        render={({ field }) => (
                                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                                            <SelectTrigger className="w-full bg-background/50 border-0 rounded-lg text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {fontFamilyOptions.map(option => (
                                                <SelectItem key={option.value} value={option.value} className="text-xs">{option.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      />
                                    </div>

                                    <div>
                                      <Label className="text-xs text-muted-foreground mb-1 block">Font Size</Label>
                                      <Controller
                                        name="font_size"
                                        control={control}
                                        render={({ field }) => (
                                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                                            <SelectTrigger className="w-full bg-background/50 border-0 rounded-lg text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {fontSizeOptions.map(option => (
                                                <SelectItem key={option.value} value={option.value} className="text-xs">{option.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      />
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                    <div>
                                      <Label className="text-xs text-muted-foreground mb-1 block">Font Color</Label>
                                      <Controller
                                        name="font_color"
                                        control={control}
                                        render={({ field }) => (
                                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                                            <SelectTrigger className="w-full bg-background/50 border-0 rounded-lg text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {fontColorOptions.map(option => (
                                                <SelectItem key={option.value} value={option.value} className="text-xs">{option.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      />
                                    </div>

                                    <div>
                                      <Label className="text-xs text-muted-foreground mb-1 block">Text Position</Label>
                                      <Controller
                                        name="text_position"
                                        control={control}
                                        render={({ field }) => (
                                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                                            <SelectTrigger className="w-full bg-background/50 border-0 rounded-lg text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {textPositionOptions.map(option => (
                                                <SelectItem key={option.value} value={option.value} className="text-xs">{option.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Generation Settings - Preserve Subject hidden but always enabled */}
                            {/* 
                            <div className="space-y-2 pt-2 border-t border-border/50">
                              <h4 className="text-sm font-bold text-card-foreground flex items-center">
                                <Settings className="w-4 h-4 mr-2" />
                                Generation Settings
                              </h4>
                              <div className="flex items-center space-x-2">
                                <Controller
                                  name="preserve_subject"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                                  )}
                                />
                                <Label className="text-xs text-muted-foreground">Preserve Subject</Label>
                              </div>
                            </div>
                            */}

                            {/* Psychological Influence Tuning */}
                            <div className="pt-2 border-t border-border/50">
                              <Dialog open={showInfluenceTuningModal} onOpenChange={setShowInfluenceTuningModal}>
                                <DialogTrigger asChild>
                                  <button
                                    type="button"
                                    className="flex items-center justify-between w-full text-left mb-3 p-2 rounded-lg hover:bg-background/50 transition-colors"
                                  >
                                    <h4 className="text-sm font-bold text-card-foreground flex items-center">
                                      <BarChart3 className="w-4 h-4 mr-2" />
                                      Psychological Influence Tuning
                                    </h4>
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </DialogTrigger>

                                <DialogContent className="max-w-lg">
                                  <DialogHeader>
                                    <DialogTitle className="flex items-center">
                                      <BarChart3 className="w-5 h-5 mr-2" />
                                      Psychological Influence Tuning
                                    </DialogTitle>
                                  </DialogHeader>

                                  <div className="space-y-3 py-2">
                                    {Object.entries(adPsychology.psychological_levers).map(([key, lever]) => (
                                      <div key={key} className="space-y-2 p-3 bg-background/50 rounded-lg border border-border/30">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center space-x-2">
                                            <Label className="text-sm font-medium text-foreground">{lever.label}</Label>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Info className="h-4 w-4 text-muted-foreground hover:text-blue-500 cursor-pointer transition-colors duration-200" />
                                              </TooltipTrigger>
                                              <TooltipContent className="max-w-xs">
                                                <p className="text-sm">{lever.description}</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </div>
                                          <span className="text-lg font-bold text-blue-600 dark:text-blue-400 min-w-[3rem] text-right">
                                            {watchedInfluenceTuning?.[key] || 0}%
                                          </span>
                                        </div>

                                        {/* Modern Slider */}
                                        <div className="relative px-1">
                                          <Controller
                                            name={`influenceTuning.${key}`}
                                            control={control}
                                            render={({ field }) => (
                                              <div className="relative">
                                                <input
                                                  type="range"
                                                  min="0"
                                                  max="100"
                                                  step="1"
                                                  value={field.value || 0}
                                                  onChange={(e) => field.onChange(parseInt(e.target.value))}
                                                  className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full appearance-none cursor-pointer slider-modern"
                                                  style={{
                                                    background: `linear-gradient(to right, 
                                                      rgb(59 130 246) 0%, 
                                                      rgb(147 51 234) ${field.value || 0}%, 
                                                      rgb(226 232 240) ${field.value || 0}%, 
                                                      rgb(226 232 240) 100%)`
                                                  }}
                                                />
                                              </div>
                                            )}
                                          />
                                        </div>
                                      </div>
                                    ))}

                                    <div className="flex justify-end pt-4">
                                      <Button
                                        type="button"
                                        onClick={() => setShowInfluenceTuningModal(false)}
                                        className="bg-primary hover:bg-primary/90"
                                      >
                                        Done
                                      </Button>
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Output Settings & Generate Button */}
                  <div className="relative border-t border-border/50 pt-2">
                    <Dialog
                      open={showOutputSettingsModal}
                      onOpenChange={(open) => {
                        if (open) {
                          // Load current values into temp state when opening
                          setTempNumImages(getValues('num_images') || 1);
                          setTempAspectRatio(getValues('aspect_ratio') || 'auto');
                          setTempResolution(getValues('resolution') || '1k');
                          setTempOmitTextOverlay(getValues('omit_text_overlay') ?? false);
                        }
                        setShowOutputSettingsModal(open);
                      }}
                    >
                      <div className="grid grid-cols-2 gap-2">
                        {/* Output Settings Button */}
                        <DialogTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-10 w-full flex items-center justify-center space-x-2 bg-background/50 rounded-lg border border-black dark:border-white hover:bg-background/70 hover:border-black dark:hover:border-white"
                          >
                            <ImageIcon className="w-4 h-4 text-black dark:text-white" />
                            <span className="font-normal text-sm text-black dark:text-white">Output Settings</span>
                          </Button>
                        </DialogTrigger>

                        {/* Generate Button */}
                        <div id="generate-button">
                          <Button
                            type="submit"
                            disabled={isEnhancing || (textOnlyMode ? false : (multiProductMode ? selectedProducts.length < 2 : selectedImage.length === 0)) || isLimitReached}
                            className={`w-full h-10 font-semibold text-sm rounded-lg shadow-lg transition-all duration-300 border-0 ${isLimitReached
                              ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60'
                              : 'bg-gradient-to-r from-primary via-purple-600 to-pink-600 hover:from-primary/90 hover:via-purple-700 hover:to-pink-700 text-primary-foreground shadow-primary/25 hover:shadow-primary/40 transform hover:scale-105 disabled:opacity-50 disabled:transform-none disabled:cursor-not-allowed'
                              }`}
                          >
                            {isEnhancing ? (
                              <div className="flex items-center space-x-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Creating...</span>
                              </div>
                            ) : isLimitReached ? (
                              <div className="flex items-center space-x-2">
                                <Zap className="w-4 h-4" />
                                <span>Limit Reached</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between w-full px-1">
                                <div className="flex items-center space-x-1">
                                  <Zap className="w-3 h-3" />
                                  <span className="text-base font-semibold">
                                    Generate
                                  </span>
                                </div>
                                <div className="flex items-center space-x-1 bg-white/20 px-1.5 py-0.5 rounded-full">
                                  <span className="text-xs font-bold">{totalCost}</span>
                                </div>
                              </div>
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Dialog Content for Output Settings */}
                      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto pb-4">
                        <DialogHeader>
                          <DialogTitle className="flex items-center">
                            <ImageIcon className="w-5 h-5 mr-2" />
                            Output Settings
                          </DialogTitle>
                        </DialogHeader>

                        <div className="space-y-4 py-4 pb-2">
                          {/* AI Model Selection */}
                          <div className="space-y-2">
                            <ModelSelector
                              value={selectedModelId}
                              onChange={setSelectedModelId}
                              disabled={isEnhancing}
                              compact={true}
                            />
                          </div>

                          {/* Number of Images */}
                          <div className="space-y-2 pt-2 border-t border-border/50">
                            <h4 className="text-sm font-bold text-card-foreground flex items-center">
                              <Layers className="w-4 h-4 mr-2" />
                              Number of Images
                            </h4>
                            <Select
                              value={String(tempNumImages)}
                              onValueChange={(value) => setTempNumImages(parseInt(value))}
                            >
                              <SelectTrigger className="w-full bg-background/50 border-0 rounded-lg text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">1 Image</SelectItem>
                                <SelectItem value="2">2 Images</SelectItem>
                                <SelectItem value="3">3 Images</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Image Settings */}
                          <div className="space-y-2 pt-2 border-t border-border/50">
                            <h4 className="text-sm font-bold text-card-foreground flex items-center">
                              <ImageIcon className="w-4 h-4 mr-2" />
                              Image Settings
                              {(currentUser?.tier?.name?.toLowerCase() === 'free' && !currentUser?.is_beta_tester) && (
                                <Crown className="w-3 h-3 ml-2 text-amber-500" />
                              )}
                            </h4>

                            {/* Free user upgrade message - only show if not using Pro model or limit exceeded */}
                            {(currentUser?.tier?.name?.toLowerCase() === 'free' && !currentUser?.is_beta_tester && (!selectedModel || selectedModel?.name !== 'gemini-pro' || !selectedModel?.usage.allowed)) && (
                              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-3">
                                <div className="flex items-start space-x-2">
                                  <Crown className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">
                                      {selectedModel?.name === 'gemini-pro' && !selectedModel?.usage.allowed
                                        ? 'Monthly Pro Model limit reached'
                                        : 'Upgrade to get access to the latest Pro Image Model'}
                                    </p>
                                    <p className="text-xs text-amber-700 dark:text-amber-300">
                                      {selectedModel?.name === 'gemini-pro' && !selectedModel?.usage.allowed
                                        ? 'Upgrade for unlimited Pro model generations with custom aspect ratios, 4K resolution, and superior image fidelity.'
                                        : 'Get access to the latest AI model with custom aspect ratios, 4K resolution, and superior image fidelity.'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                              <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">
                                  Aspect Ratio
                                  {!selectedModel?.capabilities?.aspect_ratio && (
                                    <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400">(Not available for this model)</span>
                                  )}
                                </Label>
                                <Select
                                  value={tempAspectRatio}
                                  onValueChange={setTempAspectRatio}
                                  disabled={!selectedModel?.capabilities?.aspect_ratio}
                                >
                                  <SelectTrigger className={`w-full bg-background/50 border-0 rounded-lg text-sm ${!selectedModel?.capabilities?.aspect_ratio
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                                    }`}>
                                    <SelectValue placeholder="Select ratio" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="auto" className="text-sm">Auto</SelectItem>
                                    <SelectItem value="1:1" className="text-sm">1:1 (Square)</SelectItem>
                                    <SelectItem value="4:5" className="text-sm">4:5 (Portrait)</SelectItem>
                                    <SelectItem value="16:9" className="text-sm">16:9 (Landscape)</SelectItem>
                                    <SelectItem value="9:16" className="text-sm">9:16 (Vertical Full)</SelectItem>
                                    <SelectItem value="2:3" className="text-sm">2:3 (Vertical Pin)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">
                                  Resolution
                                  {(!selectedModel?.capabilities?.aspect_ratio || !selectedModel?.capabilities?.resolution || selectedModel?.capabilities?.resolution?.length <= 1) && (
                                    <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400">(Not available for this model)</span>
                                  )}
                                </Label>
                                <Select
                                  value={tempResolution}
                                  onValueChange={setTempResolution}
                                  disabled={!selectedModel?.capabilities?.aspect_ratio || !selectedModel?.capabilities?.resolution || selectedModel?.capabilities?.resolution?.length <= 1}
                                >
                                  <SelectTrigger className={`w-full bg-background/50 border-0 rounded-lg text-sm ${(!selectedModel?.capabilities?.aspect_ratio || !selectedModel?.capabilities?.resolution || selectedModel?.capabilities?.resolution?.length <= 1)
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                                    }`}>
                                    <SelectValue placeholder="Select resolution" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1k" className="text-sm">
                                      1k ({Math.round((modelCost * 1.0) / 5) * 5} credits)
                                    </SelectItem>
                                    <SelectItem value="2k" className="text-sm">
                                      2k ({Math.round((modelCost * 1.5) / 5) * 5} credits)
                                    </SelectItem>
                                    <SelectItem value="4k" className="text-sm">
                                      4k ({Math.round((modelCost * 2.0) / 5) * 5} credits)
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {/* Text Overlay Control */}
                            <div className="mt-4 pt-4 border-t border-border/50">
                              <div className="flex items-start space-x-3">
                                <input
                                  type="checkbox"
                                  id="omit-text-overlay"
                                  checked={tempOmitTextOverlay}
                                  onChange={(e) => setTempOmitTextOverlay(e.target.checked)}
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <div className="flex-1">
                                  <label htmlFor="omit-text-overlay" className="text-sm font-medium cursor-pointer">
                                    Omit AI-generated text overlays
                                  </label>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Select this to ensure no text overlay is generated unless explicitly specified in Advanced Options.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Modal Footer Buttons */}
                          <div className="flex items-center justify-end space-x-2 pt-4 border-t border-border/50">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                // Cancel - just close without applying changes
                                setShowOutputSettingsModal(false);
                              }}
                              className="px-4"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={() => {
                                // Confirm - apply temp values to form
                                setValue('num_images', tempNumImages);
                                setValue('aspect_ratio', tempAspectRatio);
                                setValue('resolution', tempResolution);
                                setValue('omit_text_overlay', tempOmitTextOverlay);
                                setShowOutputSettingsModal(false);
                              }}
                              className="px-4 bg-primary hover:bg-primary/90"
                            >
                              Confirm
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                </div>

              </form>
            </div>

            {/* Right Panel - Canvas */}
            <div className="lg:col-span-8">
              <div id="creative-canvas" className="relative overflow-hidden rounded-xl sm:rounded-2xl lg:rounded-3xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border border-gray-200 shadow-xl shadow-slate-500/5 min-h-[300px] sm:min-h-[400px] lg:min-h-[500px]">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 via-primary/5 to-purple-500/5"></div>

                {/* Canvas Header */}
                <div className="relative border-b border-border/30 p-2 sm:p-4 lg:p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-md sm:rounded-lg lg:rounded-xl flex items-center justify-center">
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-white" />
                      </div>
                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-card-foreground">Image Preview</h3>
                      {generatedEnhancedImages.length > 0 && hasPersistedState() && !isEnhancing && (
                        <div className="flex items-center space-x-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                          <Save className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Restored</span>
                        </div>
                      )}
                    </div>
                    {generatedEnhancedImages.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => handleDownload()} className="text-muted-foreground text-xs sm:text-sm">
                          <Download className="w-4 h-4 mr-1 sm:mr-2" />
                          Download All
                        </Button>
                        <Button size="sm" onClick={() => handleCreatePost()} className="hidden bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs sm:text-sm">
                          <Share2 className="w-4 h-4 mr-1 sm:mr-2" />
                          Create Post
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Canvas Content */}
                <div className="relative p-3 sm:p-6 flex items-center justify-center min-h-[350px] lg:min-h-[550px] overflow-y-auto">
                  {isEnhancing ? (
                    <div className="text-center space-y-6 sm:space-y-8">
                      {/* Sleek Circular Progress Indicator */}
                      <div className="relative w-28 h-28 sm:w-36 sm:h-36 mx-auto">
                        {/* Background circle with subtle glow */}
                        <svg className="w-full h-full transform -rotate-90 drop-shadow-sm" viewBox="0 0 100 100">
                          <circle
                            cx="50"
                            cy="50"
                            r="42"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                            className="text-slate-100 dark:text-slate-800"
                          />
                          {/* Progress circle with gradient effect */}
                          <circle
                            cx="50"
                            cy="50"
                            r="42"
                            stroke="url(#progressGradient)"
                            strokeWidth="4"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 42}`}
                            strokeDashoffset={`${2 * Math.PI * 42 * (1 - generationProgress / 100)}`}
                            className="transition-all duration-700 ease-out filter drop-shadow-sm"
                            strokeLinecap="round"
                          />
                          {/* Gradient definition */}
                          <defs>
                            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="rgb(99, 102, 241)" />
                              <stop offset="50%" stopColor="rgb(139, 92, 246)" />
                              <stop offset="100%" stopColor="rgb(236, 72, 153)" />
                            </linearGradient>
                          </defs>
                        </svg>

                        {/* Center content - Quixocial logo with cool animations */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="relative">
                            {/* Main logo with subtle scaling animation */}
                            <img
                              src="/icon-light.png"
                              alt="Quixocial"
                              className="w-14 h-14 sm:w-16 sm:h-16 animate-pulse"
                            />

                            {/* Rotating glow rings around the logo */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-12 h-12 sm:w-14 sm:h-14 border-2 border-blue-400/30 rounded-full animate-spin" style={{ animationDuration: '8s' }}></div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-16 h-16 sm:w-18 sm:h-18 border border-purple-400/20 rounded-full animate-spin" style={{ animationDuration: '12s', animationDirection: 'reverse' }}></div>
                            </div>

                            {/* Pulsing glow effect behind logo */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-lg animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xl sm:text-2xl font-bold text-card-foreground animate-pulse">
                          {generationMessage}
                        </h3>
                        <p className="text-muted-foreground max-w-md text-sm sm:text-base">
                          {textOnlyMode
                            ? `Generating ${numImages} image${numImages > 1 ? 's' : ''}`
                            : `Generating ${numImages} image${numImages > 1 ? 's' : ''}`
                          }
                        </p>
                      </div>
                    </div>
                  ) : generatedEnhancedImages.length > 0 ? (
                    <div className="w-full h-full flex items-center justify-center p-4">
                      {generatedEnhancedImages.length === 1 ? (
                        // Single image display
                        <div className="relative group w-full max-w-2xl h-full flex items-center justify-center">
                          <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl max-h-full">
                            <AuthenticatedImage
                              src={generatedEnhancedImages[0]}
                              alt="Generated content"
                              className="max-w-full max-h-[350px] lg:max-h-[500px] object-contain"
                              clientId={currentClient?.id}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>

                          {/* Floating Action Buttons */}
                          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <Button size="sm" variant="secondary" onClick={() => handleDownload()} className="backdrop-blur-sm bg-card/90">
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button size="sm" onClick={() => handleCreatePost()} className="hidden backdrop-blur-sm bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                              <Share2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Multiple images grid
                        <div className="w-full max-w-6xl mx-auto">
                          <div className="mb-6 text-center">
                            <h4 className="text-xl font-semibold text-card-foreground">Generated Variations</h4>
                            <p className="text-sm text-muted-foreground">All variations were saved to Media Library</p>
                          </div>
                          <div className={`grid gap-2 sm:gap-4 ${generatedEnhancedImages.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                            generatedEnhancedImages.length === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :
                              'grid-cols-1'
                            }`}>
                            {generatedEnhancedImages.map((imageUrl, index) => (
                              <div key={index} className="relative group">
                                <div className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/5">
                                  {/* Image counter badge */}
                                  <div className="absolute top-3 left-3 z-10 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded-full">
                                    {index + 1}
                                  </div>
                                  <div className="aspect-[4/5] flex items-center justify-center p-3">
                                    <AuthenticatedImage
                                      src={imageUrl}
                                      alt={`Generated content ${index + 1}`}
                                      className="max-w-full max-h-full object-contain rounded-lg"
                                      clientId={currentClient?.id}
                                    />
                                  </div>
                                  {/* Floating Action Buttons - Top Right */}
                                  <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={() => handleDownload(imageUrl, index)}
                                      className="backdrop-blur-sm bg-white/90 hover:bg-white text-slate-800 shadow-lg"
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleCreatePost(imageUrl)}
                                      className="hidden backdrop-blur-sm bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg"
                                    >
                                      <Share2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="mt-2 text-center">
                                  <span className="text-xs text-muted-foreground">Variation {index + 1}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center space-y-6 sm:space-y-8">
                      <div className="relative w-32 h-32 sm:w-40 sm:h-40 mx-auto">
                        <div className="w-full h-full bg-gradient-to-br from-muted to-accent rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-inner">
                          <ImageIcon className="w-16 h-16 sm:w-20 sm:h-20 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="space-y-3 sm:space-y-4">
                        <h3 className="text-2xl sm:text-3xl font-bold text-card-foreground">Image Preview</h3>
                        <p className="text-muted-foreground max-w-lg text-sm sm:text-base">
                          {textOnlyMode 
                            ? "Choose your purpose and add optional details. Our AI will create a professional content that showcases your expertise and builds trust."
                            : "Upload your product image and choose your creative style. Our AI will transform it into a stunning creative ready for posting."
                          }
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-muted-foreground pt-2">
                          <div className="flex items-center space-x-2">
                            <Heart className="w-4 h-4 text-red-500" />
                            <span>AI-Powered</span>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Layers className="w-4 h-4 text-blue-500" />
                            <span>Pro Quality</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Slider Styles & Tooltip Escape Styles */}
      <style jsx>{`
        .slider-modern {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          border-radius: 4px;
          outline: none;
          transition: all 0.3s ease;
        }
        
        .slider-modern::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgb(59 130 246), rgb(147 51 234));
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .slider-modern::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        
        .slider-modern::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgb(59 130 246), rgb(147 51 234));
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        /* Tooltip Escape Styles - Ensure tooltips appear over all containers */
        :global([data-radix-popper-content-wrapper]) {
          z-index: 999999 !important;
          position: fixed !important;
          contain: none !important;
        }
        
        :global([data-radix-tooltip-content]) {
          z-index: 999999 !important;
          position: fixed !important;
          contain: none !important;
        }

        /* Force tooltip portal to escape all containers */
        :global(body > div[data-radix-portal]) {
          z-index: 999999 !important;
          position: fixed !important;
        }

        /* Override any container overflow that might clip tooltips */
        :global(.ad-studio-form) {
          contain: none !important;
        }
        
        :global(#upload-section) {
          contain: none !important;
          overflow: visible !important;
        }
        
        .slider-modern::-moz-range-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        
        .slider-modern::-moz-range-track {
          height: 8px;
          border-radius: 4px;
          border: none;
        }
        
        .dark .slider-modern::-webkit-slider-thumb {
          border: 2px solid rgb(30 41 59);
        }
        
        .dark .slider-modern::-moz-range-thumb {
          border: 2px solid rgb(30 41 59);
        }
      `}</style>

      {/* Illustration Styles Modal */}
      <Dialog open={showIllustrationModal} onOpenChange={setShowIllustrationModal}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Choose Illustration Style</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
            {illustrationStyleOptions.map((option) => (
              <div
                key={option.value}
                className="group cursor-pointer"
                onClick={() => {
                  setValue('styleArtDirection', option.value);
                  setShowIllustrationModal(false);
                }}
              >
                <div className="relative overflow-hidden rounded-lg border-2 border-transparent group-hover:border-primary transition-all duration-200">
                  <div className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src={option.thumbnail}
                      alt={option.label}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      onError={(e) => {
                        // Fallback to a placeholder if image fails to load
                        e.currentTarget.src = '/images/thumbnails/placeholder.png';
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 rounded-lg" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 rounded-b-lg">
                    <h3 className="text-white font-medium text-sm">{option.label}</h3>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {option.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fallback Model Dialog */}
      <Dialog open={showFallbackDialog} onOpenChange={setShowFallbackDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Premium Model Temporarily Unavailable</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Our premium AI model (Nano Banana Pro) is currently experiencing high demand and is temporarily unavailable.
            </p>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-2">
                Would you like to use the Standard model (Nano-Banana) version for this generation instead?
              </p>
              <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1 ml-4">
                <li>• Faster generations but lower accuracy and fidelity generations</li>
                <li>• No aspect ratio or resolution control</li>
              </ul>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowFallbackDialog(false);
                  setFallbackTaskId(null);
                }}
              >
                Wait & Try Later
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500"
                onClick={async () => {
                  if (!fallbackTaskId) return;

                  try {
                    setShowFallbackDialog(false);
                    setIsEnhancing(true);

                    const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks/${fallbackTaskId}/retry-with-fallback`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                        'Content-Type': 'application/json',
                        'X-Client-ID': currentClient?.id || '',
                      },
                    });

                    if (response.ok) {
                      const newTask = await response.json();
                      toast({
                        title: "Generation Started",
                        description: "Using Gemini 2.5 Flash fallback model...",
                      });
                      // Start polling for the new task
                      pollTaskStatus(newTask.id);
                    } else {
                      throw new Error('Failed to retry with fallback');
                    }
                  } catch (error) {
                    setIsEnhancing(false);
                    toast({
                      title: "Retry Failed",
                      description: "Unable to retry with fallback model. Please try again later.",
                      variant: "destructive",
                    });
                  }
                  setFallbackTaskId(null);
                }}
              >
                Use Standard Model
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default AdStudioPage;